// Redesigned Home.js with week-view calendar and card-based timeline
// UPDATED: Added registration status visual indicators with GREEN border for ongoing events
import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from '../contexts/LanguageContext';
import { db } from "../firebase";
import ShareModal from "../utils/ShareModal";
import { shareToWhatsApp, shareToFacebook, copyEventToClipboard } from "../utils/shareUtils";
import { getEarlyBirdStatus, getEventCounts, fetchUserAvatars } from "../utils/eventHelpers";
import EventGroupsEditor from "./EventGroupsEditor";
import { normalizeGroups, validateGroups, totalGroupCapacity, eventGroups, groupStats, groupHasPrice } from "../utils/eventGroups";
// PERF: createPromotionNotification is loaded on demand (dynamic import at the
// call site) — a static import here pulled the 8,000-line AdminDashboard into
// the first-load bundle for every visitor.
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    getDoc,
    doc,
    updateDoc,
    deleteDoc,
    addDoc,
    increment,
    arrayUnion,
    arrayRemove,
    onSnapshot,
} from "firebase/firestore";

// PERF: recharts (~100KB gzipped) is only needed for the
// "Registrations / hour" mini chart, so it is code-split and loaded on demand.
const RegistrationsBarChart = lazy(() => import('./RegistrationsBarChart'));

// Add scrollbar-hide utility
const scrollbarHideStyle = `
    .scrollbar-hide::-webkit-scrollbar {
        display: none;
    }
    .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
`;

// Inject style
if (typeof document !== 'undefined') {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = scrollbarHideStyle;
    document.head.appendChild(styleTag);
}

// Overlapping participant avatars (facepile) with a "+N" overflow bubble.
function ParticipantFacepile({ uids = [], avatarMap = {}, totalCount = 0, max = 4 }) {
    if (!totalCount || totalCount <= 0) return null;
    const shown = uids.slice(0, max);
    const overflow = Math.max(0, totalCount - shown.length);
    const COLORS = ["bg-sky-400","bg-emerald-400","bg-violet-400","bg-pink-400","bg-amber-400","bg-orange-400"];
    const colorFor = (uid) => COLORS[(uid.charCodeAt(0) + uid.charCodeAt(uid.length - 1)) % COLORS.length];
    return (
        <div className="flex -space-x-2 items-center">
            {shown.map((uid) => {
                const info = avatarMap[uid];
                return info && info.photoURL ? (
                    <img
                        key={uid}
                        src={info.photoURL}
                        alt={info.name || "Player"}
                        className="w-7 h-7 rounded-full border-2 border-white object-cover bg-gray-100"
                    />
                ) : (
                    <div
                        key={uid}
                        className={`w-7 h-7 rounded-full border-2 border-white ${colorFor(uid)} flex items-center justify-center text-white text-[10px] font-bold`}
                    >
                        {info?.name ? info.name.charAt(0).toUpperCase() : "·"}
                    </div>
                );
            })}
            {overflow > 0 && (
                <div className="w-7 h-7 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-gray-600 text-[10px] font-semibold">
                    +{overflow}
                </div>
            )}
        </div>
    );
}

// Month-grid calendar/timetable view. Days with no events render light grey
// and are not clickable; days with events are highlighted and tappable —
// tapping jumps the parent into day-filtered list view for that date.
// Waitlist-promotion countdown box on an event card. Owns its own 1-second
// interval so only this tiny component re-renders each tick, not Home.
// Renders nothing once the deadline has passed.
function PromotionCountdown({ eventId, deadline, lang }) {
    const navigate = useNavigate();
    const deadlineMs = new Date(deadline).getTime();
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!deadlineMs || Number.isNaN(deadlineMs) || deadlineMs <= Date.now()) return undefined;
        const iv = setInterval(() => {
            const n = Date.now();
            setNow(n);
            if (n >= deadlineMs) clearInterval(iv);
        }, 1000);
        return () => clearInterval(iv);
    }, [deadlineMs]);
    const left = deadlineMs - now;
    if (!deadlineMs || Number.isNaN(deadlineMs) || left <= 0) return null;
    const totalSec = Math.floor(left / 1000);
    const pad2 = (n) => String(n).padStart(2, '0');
    const hms = `${pad2(Math.floor(totalSec / 3600))}:${pad2(Math.floor((totalSec % 3600) / 60))}:${pad2(totalSec % 60)}`;
    return (
        <div className="mb-3 p-2.5 rounded-lg bg-orange-50 border border-orange-300" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs font-semibold text-orange-800 mb-2">
                {lang === 'zh' ? `有空位了！请在 ${hms} 内付款` : `Spot available — pay within ${hms}`}
            </p>
            <button
                type="button"
                className="w-full px-3 py-1.5 rounded-md bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold"
                onClick={(e) => { e.stopPropagation(); navigate(`/event/${eventId}?pay=1`); }}
            >{lang === 'zh' ? '立即付款' : 'Pay now'}</button>
        </div>
    );
}

// Full event card — used by both the main list feed and the calendar's
// inline "selected day" panel, so both views always render identically
// (status badges, facepile, spots left, admin actions, everything).
function EventCard({ event, venues, avatarMap, currentUser, isAdmin, t,
    getEventRegistration, getEventStatus, onCardClick,
    handleMarkAsFull, handleEditEvent, handleDuplicateEvent, handleDeleteEvent, handleShare, handleToggleHideEvent,
    duplicatingEvent, hasDanglingPayment = false, activeChallenges = [],
    lang = 'en', pendingPromotion = null }) {
    const eventVenue = venues.find(v => v.id === event.venueId);
    const registration = getEventRegistration(event.id);
    const isRegistered = !!registration;
    const eventStatus = getEventStatus(event);

    // Auto re-render when early bird expires; tick every second when < 5min
    const [, setTick] = useState(0);
    useEffect(() => {
        const { isEarlyBird, earlyBirdEndsAt } = getEarlyBirdStatus(event);
        if (!isEarlyBird || !earlyBirdEndsAt) return;
        const ms = earlyBirdEndsAt - Date.now();
        if (ms <= 0) return;
        if (ms <= 300000) {
            // Under 5 min: tick every second for live countdown
            const interval = setInterval(() => {
                if (earlyBirdEndsAt - Date.now() <= 0) { clearInterval(interval); }
                setTick(t => t + 1);
            }, 1000);
            return () => clearInterval(interval);
        }
        const timer = setTimeout(() => setTick(t => t + 1), ms - 300000 + 500);
        return () => clearTimeout(timer);
    }, [event.earlyBird, event.startDate]);

    const isOnWaitlist = registration?.status === "waitlisted" ||
                        registration?.paymentStatus === "waitlisted" ||
                        event?.waitlist?.includes(currentUser?.uid);

    // Waitlist position (1-based) for the logged-in user; 0 when not on it.
    const waitlistPos = currentUser?.uid ? ((event?.waitlist || []).indexOf(currentUser.uid) + 1) : 0;

    // Signed up via Mini Program pay-later but not yet paid — surface a
    // "Payment due" badge so the user knows to complete payment. Keyed on
    // paymentDueBy so only pay-later signups (not bank-transfer pending)
    // get the urgent styling.
    const isPendingPayment = registration?.paymentStatus === "pending" &&
                             !!registration?.paymentDueBy &&
                             registration?.status !== "cancelled";

    const cs = event.cardStyle || {};
    const hasDash = cs.borderDash && cs.borderColor;
    const borderColor = cs.borderColor || '#f97316';
    const marqueeSpeed = cs.marqueeSpeed || 'normal';

    return (
        <div
            onClick={() => onCardClick(event.id)}
            className={`rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden relative bg-white ${hasDash ? 'dashed-border-animated' : getEarlyBirdStatus(event).isEarlyBird ? 'border-l-[3px] border border-gray-100' : 'border border-gray-100'} ${event.hiddenFromList && isAdmin ? 'opacity-60' : ''}`}
            style={hasDash ? { '--cbd': borderColor } : getEarlyBirdStatus(event).isEarlyBird ? { borderLeftColor: 'rgb(235,117,29)' } : {}}
        >
            {/* Admin: hidden badge */}
            {event.hiddenFromList && isAdmin && (
                <div className="absolute top-1.5 right-1.5 z-10 px-1.5 py-0.5 rounded-full bg-gray-800 bg-opacity-70 text-white text-[10px] font-semibold flex items-center gap-0.5">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/></svg>
                    Hidden
                </div>
            )}
            {/* Early bird corner ribbon - removed, using price box instead */}
            {/* Marching-ants marquee banner */}
            {cs.marqueeText && (
                <div className="overflow-hidden bg-opacity-10 relative h-6" style={{ backgroundColor: borderColor + '22' }}>
                    <span
                        className={`absolute whitespace-nowrap text-xs font-semibold marquee-${marqueeSpeed}`}
                        style={{ color: borderColor, top: '4px' }}
                    >
                        {cs.marqueeText}
                    </span>
                </div>
            )}
            {/* Dangling payment "?" badge — top-right corner */}
            {hasDanglingPayment && (
                <div className="absolute bottom-2 right-2 z-10 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold shadow-md animate-pulse"
                    title="Payment issue — tap to check">
                    ?
                </div>
            )}
            <div className="px-3 py-2.5">
                {/* Status badges row */}
                {(eventStatus === 'ongoing' || isRegistered || waitlistPos > 0) && (
                    <div className="flex gap-2 mb-3 flex-wrap">
                        {eventStatus === 'ongoing' && (
                            <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-300 rounded-md">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                                <span className="text-xs font-semibold text-green-700">{t('common.inProgress')}</span>
                            </div>
                        )}
                        {(isRegistered || waitlistPos > 0) && (
                            // Waitlist position pill replaces the generic waitlisted badge
                            // (skipped while a promotion is pending — the box below covers it).
                            waitlistPos > 0 && !pendingPromotion ? (
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-300 rounded-full">
                                    <span className="text-xs font-semibold text-amber-700">
                                        {lang === 'zh' ? `候补第 ${waitlistPos} 位` : `Waitlist #${waitlistPos}`}
                                    </span>
                                </div>
                            ) : isOnWaitlist ? (
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-50 border border-yellow-300 rounded-md">
                                    <span className="text-xs font-semibold text-yellow-700">{t('home.waitlistedHourglass')}</span>
                                </div>
                            ) : isPendingPayment ? (
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 border border-orange-300 rounded-md">
                                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></span>
                                    <span className="text-xs font-semibold text-orange-700">Payment due</span>
                                </div>
                            ) : (
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-300 rounded-md">
                                    <span className="text-xs font-semibold text-blue-700">{t('home.registeredTick')}</span>
                                </div>
                            )
                        )}
                    </div>
                )}

                {/* Pending waitlist promotion — live countdown + pay button */}
                {pendingPromotion && (
                    <PromotionCountdown
                        eventId={event.id}
                        deadline={pendingPromotion.promotionExpiresAt || event.startDate}
                        lang={lang}
                    />
                )}

                {/* 🔥 Active challenge banner */}
                {(() => {
                    const liveChallenges = activeChallenges.filter(ch => ch.status === 'active');
                    // Check if current user is in any pending or active challenge
                    const uid = currentUser?.uid;
                    const myChallenges = uid ? activeChallenges.filter(ch => {
                        const allPlayers = [...(ch.teamA || []), ...(ch.teamB || [])];
                        return allPlayers.some(p => p.userId === uid || p.uid === uid);
                    }) : [];
                    const myPending = myChallenges.filter(ch => ch.status === 'pending');
                    const myLive = myChallenges.filter(ch => ch.status === 'active');

                    return (
                        <>
                            {/* Personal challenge alert — you're in a challenge */}
                            {myLive.length > 0 && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 mb-2 rounded-lg bg-gradient-to-r from-red-50 to-orange-50 border border-red-200">
                                    <span className="text-base">⚡</span>
                                    <span className="text-xs font-bold text-red-700">
                                        Your match is LIVE right now!
                                    </span>
                                </div>
                            )}
                            {myPending.length > 0 && myLive.length === 0 && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 mb-2 rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200">
                                    <span className="text-base">🏸</span>
                                    <span className="text-xs font-bold text-amber-700">
                                        You have {myPending.length === 1 ? 'an upcoming challenge' : `${myPending.length} upcoming challenges`}!
                                    </span>
                                </div>
                            )}
                            {/* General live challenges (only if user not already shown personal) */}
                            {liveChallenges.length > 0 && myLive.length === 0 && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 mb-2 rounded-lg bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200">
                                    <span className="text-base animate-pulse">🔥</span>
                                    <span className="text-xs font-bold text-orange-700">
                                        {liveChallenges.length === 1
                                            ? `Challenge LIVE — ${(() => {
                                                const ch = liveChallenges[0];
                                                const a = (ch.teamA || []).map(p => p.name).join(' & ');
                                                const b = (ch.teamB || []).map(p => p.name).join(' & ');
                                                return `${a} vs ${b}`;
                                            })()}`
                                            : `${liveChallenges.length} Challenges LIVE right now!`}
                                    </span>
                                </div>
                            )}
                        </>
                    );
                })()}

                {(() => {
                    const { effectivePrice, isEarlyBird, earlyBirdEndsAt } = getEarlyBirdStatus(event);
                    const start = new Date(event.startDate);
                    const end = new Date(event.endDate);
                    const month = start.getMonth() + 1;
                    const day = start.getDate();
                    const venueName = eventVenue?.name || event.venueName || event.location || '';
                    const fmt = (d) => {
                        const h = d.getHours();
                        const m = d.getMinutes();
                        const ampm = h >= 12 ? 'pm' : 'am';
                        const hh = h > 12 ? h - 12 : h === 0 ? 12 : h;
                        return m === 0 ? `${hh}${ampm}` : `${hh}.${String(m).padStart(2,'0')}${ampm}`;
                    };
                    const durationH = (end - start) / 3600000;
                    const durLabel = durationH % 1 === 0 ? `${durationH}h` : `${durationH}h`;

                    if (isEarlyBird) {
                        let countdown = '';
                        if (earlyBirdEndsAt) {
                            const diff = earlyBirdEndsAt - new Date();
                            if (diff > 0) {
                                const d = Math.floor(diff / 86400000);
                                const h = Math.floor((diff % 86400000) / 3600000);
                                const mi = Math.floor((diff % 3600000) / 60000);
                                const secs = Math.floor((diff % 60000) / 1000);
                                countdown = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${mi}m` : mi > 0 ? `${mi}m ${secs}s` : `${secs}s`;
                            }
                        }
                        return (
                            <div className="mb-2">
                                <div className="float-right ml-2.5 mb-1 w-[98px] rounded-lg border-2 overflow-hidden shadow-sm" style={{borderColor:'rgb(235,117,29)', backgroundColor:'rgb(235,117,29)'}}>
                                    <div className="text-center py-0.5">
                                        <span className="text-[9px] font-black text-white tracking-widest">EARLY BIRD</span>
                                    </div>
                                    <div className="flex items-baseline justify-center gap-1 py-1 bg-white">
                                        <span className="text-base font-black" style={{color:'rgb(235,117,29)'}}>${effectivePrice}</span>
                                        <span className="text-[11px] text-gray-400 line-through">${event.price}</span>
                                    </div>
                                    {countdown && (
                                        <div className="flex items-center justify-center gap-1 py-0.5 border-t bg-white" style={{borderColor:'rgba(235,117,29,0.15)'}}>
                                            <svg className="w-2.5 h-2.5 opacity-60" style={{color:'rgb(235,117,29)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                            <span className="text-[9px] font-semibold" style={{color:'rgb(180,80,10)'}}>Ends {countdown}</span>
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-sm font-bold text-gray-900 leading-snug">
                                    <span>{month}.{day} {venueName}</span>
                                    <br/>
                                    <span className="font-normal text-gray-600">{fmt(start)}–{fmt(end)} ({durLabel})</span>
                                </h3>
                            </div>
                        );
                    } else {
                        return (
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <h3 className="text-sm font-bold text-gray-900 leading-snug flex-1">
                                    <span>{month}.{day} {venueName}</span>
                                    <br/>
                                    <span className="font-normal text-gray-600">{fmt(start)}–{fmt(end)} ({durLabel})</span>
                                </h3>
                                <div className="flex-shrink-0 text-right relative">
                                    {event.isFreeEvent === true ? (
                                        // Admin-customisable (Edit Event → Free event → card label);
                                        // can also be hidden entirely.
                                        event.hideFreeLabel ? null : (
                                            <span className="text-sm font-extrabold text-emerald-600">{(event.freeLabel || '').trim() || 'FREE 免费'}</span>
                                        )
                                    ) : (
                                        <span className="text-base font-extrabold text-orange-500">
                                            ${effectivePrice}
                                        </span>
                                    )}
                                    {event.originalPrice && event.originalPrice > event.price && (
                                        <span className="text-xs text-gray-400 line-through ml-1">${event.originalPrice}</span>
                                    )}
                                </div>
                            </div>
                        );
                    }
                })()}

                {/* Info rows — only non-redundant details */}
                <div className="space-y-1 mb-2">
                    {/* Address only (venue name already in title) */}
                    {(() => {
                        const addr = eventVenue?.address || event.address || '';
                        const short = addr.replace(/,?\s*(VIC|NSW|QLD|SA|WA|TAS|ACT|NT)\s*\d{4}/i, '').trim();
                        return short ? (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span>{short}</span>
                            </div>
                        ) : null;
                    })()}

                    {/* Organizer */}
                    {event.organizer && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>{event.organizer}</span>
                        </div>
                    )}

                    {/* Participants + views */}
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                        <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {/* All three capacity figures on this card now come from
                            getEventCounts, so "joined", the facepile total and
                            "spots left" can no longer disagree with each other.
                            They previously used three different formulas. */}
                        <span>{getEventCounts(event).occupied}/{getEventCounts(event).max} {t('home.joined')} · {event.viewCount || 0} {t('home.views')}</span>
                    </div>
                </div>

                {/* Facepile + spots */}
                <div className="flex items-center justify-between gap-2">
                    <ParticipantFacepile
                        uids={event.participants || []}
                        avatarMap={avatarMap}
                        totalCount={getEventCounts(event).joined}
                        max={8}
                    />
                    {(() => {
                        const counts = getEventCounts(event);
                        return counts.spotsLeft > 0 ? (
                            <span className="text-xs font-semibold text-green-600">{counts.spotsLeft} spots left</span>
                        ) : (
                            <span className="text-xs font-semibold text-orange-500">{t('home.full')}</span>
                        );
                    })()}
                </div>

                {/* Groups: seats left per group */}
                {eventGroups(event).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {eventGroups(event).map((g) => {
                            const st = groupStats(event, g);
                            return (
                                <span key={g.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${st.full ? 'bg-gray-50 border-gray-200 text-gray-400' : 'bg-violet-50 border-violet-200 text-violet-700'}`}>
                                    {g.name}
                                    {groupHasPrice(g) && !event.isFreeEvent && <span className="font-bold">${Number(g.price)}</span>}
                                    <span className={st.full ? 'text-orange-500' : 'text-green-600'}>
                                        {st.full ? (lang === 'zh' ? '已满' : 'Full') : (lang === 'zh' ? `余${st.left}` : `${st.left} left`)}
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Admin Quick Actions (only show for admin) */}
            {isAdmin && (
                <div className="border-t border-gray-100 px-4 py-2 flex gap-2 bg-gray-50 flex-wrap">
                    <button
                        onClick={(e) => handleMarkAsFull(event, e)}
                        className={`flex-1 min-w-0 px-2 py-2 text-xs font-medium border rounded-lg transition-colors ${
                            event.markedAsFull
                                ? 'text-orange-600 border-orange-300 bg-orange-50 hover:bg-orange-100'
                                : 'text-orange-600 border-orange-300 hover:bg-orange-50'
                        }`}
                        title={event.markedAsFull ? "Unmark as full" : "Mark as full"}
                    >
                        {event.markedAsFull ? '🔓' : '🔒'}
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleToggleHideEvent(event); }}
                        className={`flex-1 min-w-0 px-2 py-2 text-xs font-medium border rounded-lg transition-colors ${
                            event.hiddenFromList
                                ? 'text-teal-600 border-teal-300 bg-teal-50 hover:bg-teal-100'
                                : 'text-gray-600 border-gray-300 hover:bg-gray-100'
                        }`}
                        title={event.hiddenFromList ? "Show in list" : "Hide from list"}
                    >
                        {event.hiddenFromList ? (
                            <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        ) : (
                            <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
                        )}
                    </button>
                    <button
                        onClick={(e) => handleEditEvent(event, e)}
                        className="flex-1 min-w-0 px-2 py-2 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                        修改
                    </button>

                    <button
                        onClick={(e) => handleDuplicateEvent(event, e)}
                        className="flex-1 min-w-0 px-2 py-2 text-xs font-medium text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors"
                        disabled={duplicatingEvent === event.id}
                    >
                        {duplicatingEvent === event.id ? "..." : "复制"}
                    </button>
                    <button
                        onClick={(e) => handleDeleteEvent(event, e)}
                        className="flex-1 min-w-0 px-2 py-2 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                    >
                        删除
                    </button>
                    <button
                        onClick={(e) => handleShare(event, e)}
                        className="flex-1 min-w-0 px-2 py-2 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        分享
                    </button>
                </div>
            )}
        </div>
    );
}

// Skeleton placeholder shaped like a real EventCard, shown while the first
// batch of events is still loading. `delay` staggers each card's fade-in so
// the list feels like it's "filling in" rather than popping all at once.
function EventCardSkeleton({ delay = 0, showAdminRow = false }) {
    // Each gray placeholder bar gets the shimmer class instead of the whole
    // card pulsing — a moving highlight sweeps across, but nothing ever dims,
    // so there's no "blink" sensation, just a sense of motion.
    const bar = "skeleton-shimmer rounded";
    return (
        <div
            className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
            style={{
                opacity: 0,
                animation: `skeletonFadeIn 0.4s ease-out ${delay}ms forwards`
            }}
        >
            <div className="px-3 py-2.5">
                {/* Title block: two lines + price */}
                <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1">
                        <div className={`h-3.5 ${bar} w-3/5 mb-2`} />
                        <div className={`h-3 ${bar} w-2/5`} />
                    </div>
                    <div className={`h-4 ${bar} w-10 flex-shrink-0`} />
                </div>

                {/* Info rows */}
                <div className="space-y-2 mb-3">
                    <div className={`h-2.5 ${bar} w-1/2`} />
                    <div className={`h-2.5 ${bar} w-2/5`} />
                </div>

                {/* Facepile + spots */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex -space-x-2">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="w-7 h-7 rounded-full border-2 border-white skeleton-shimmer" />
                        ))}
                    </div>
                    <div className={`h-2.5 ${bar} w-16`} />
                </div>
            </div>

            {showAdminRow && (
                <div className="border-t border-gray-100 px-4 py-2 flex gap-2 bg-gray-50">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex-1 h-7 skeleton-shimmer rounded-lg" />
                    ))}
                </div>
            )}
        </div>
    );
}

// Inject skeleton keyframes + shimmer style once
if (typeof document !== 'undefined' && !document.getElementById('skeletonKeyframes')) {
    const skeletonStyleTag = document.createElement('style');
    skeletonStyleTag.id = 'skeletonKeyframes';
    skeletonStyleTag.innerHTML = `
        @keyframes skeletonFadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes skeletonShimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
        }
        .skeleton-shimmer {
            background-color: #e5e7eb;
            background-image: linear-gradient(
                90deg,
                #e5e7eb 0%,
                #e5e7eb 35%,
                #f3f4f6 50%,
                #e5e7eb 65%,
                #e5e7eb 100%
            );
            background-size: 200% 100%;
            animation: skeletonShimmer 1.8s ease-in-out infinite;
        }
        @keyframes cardFadeIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
        }
        /* Gravity-card resting/settled state and the "falling" state it
           transitions from. The cubic-bezier here overshoots slightly past
           1 before settling back — that little overshoot-and-settle is what
           reads as weight/gravity rather than a plain linear slide. */
        .gravity-card {
            transition: transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1),
                        opacity 0.4s ease-out;
        }
        .gravity-card--falling {
            opacity: 0;
            transform: translateY(22px) scale(0.985);
        }
        .gravity-card--settled {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    `;
    document.head.appendChild(skeletonStyleTag);
}

// Shared IntersectionObserver instance — one observer for the whole page
// instead of one per card. Each GravityCard registers/unregisters its own
// DOM node and callback; the observer just dispatches to whichever node
// crossed the threshold. This scales fine even with long event lists.
let sharedGravityObserver = null;
const gravityCallbacks = new WeakMap();

function getGravityObserver() {
    if (typeof window === 'undefined') return null;
    if (!sharedGravityObserver) {
        sharedGravityObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const cb = gravityCallbacks.get(entry.target);
                    if (cb) cb(entry.isIntersecting);
                });
            },
            { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
        );
    }
    return sharedGravityObserver;
}

// Wraps a card so it "settles into place" every time it scrolls into
// view — whether scrolling down (card rises up from below) or up (card
// drops back down from above) — and un-settles (resets to the "falling"
// starting state) when it scrolls back out of view, so the animation is
// ready to replay the next time it re-enters either direction. Unlike a
// one-shot reveal, this keeps observing for the card's whole lifetime
// rather than disconnecting after the first settle.
function GravityCard({ children }) {
    const ref = useRef(null);
    const [settled, setSettled] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;

        const observer = getGravityObserver();
        if (!observer) {
            // No IntersectionObserver support — just show the card, no effect.
            setSettled(true);
            return undefined;
        }

        gravityCallbacks.set(node, (isIntersecting) => {
            setSettled(isIntersecting);
        });

        // Delay observing by two animation frames. A single rAF can, on
        // fast devices, still coalesce into the same frame as the initial
        // render — the double-rAF is the standard robust pattern for
        // guaranteeing the browser has actually painted once (the
        // "falling" starting state) before anything can transition away
        // from it. Without this, a card already inside the viewport on
        // first paint (true for most/all cards above the fold) can flip
        // straight to "settled" with nothing for the transition to
        // animate from, so it's visually skipped entirely.
        let cancelled = false;
        let rafId2 = null;
        const rafId1 = requestAnimationFrame(() => {
            rafId2 = requestAnimationFrame(() => {
                if (!cancelled) observer.observe(node);
            });
        });

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafId1);
            if (rafId2 !== null) cancelAnimationFrame(rafId2);
            observer.unobserve(node);
            gravityCallbacks.delete(node);
        };
    }, []);

    return (
        <div
            ref={ref}
            className={`gravity-card ${settled ? 'gravity-card--settled' : 'gravity-card--falling'}`}
        >
            {children}
        </div>
    );
}

function CalendarMonthView({ month, onMonthChange, eventsByDay, selectedDay, onSelectDay, onEventClick,
    venues, avatarMap, currentUser, isAdmin, getEventRegistration, getEventStatus,
    handleMarkAsFull, handleEditEvent, handleDuplicateEvent, handleDeleteEvent, handleShare, handleToggleHideEvent, duplicatingEvent,
    danglingPaymentEventIds, activeChallengesMap = {}, t, lang, pendingPromotionsMap = {} }) {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const firstOfMonth = new Date(year, monthIndex, 1);
    const startWeekday = firstOfMonth.getDay(); // 0 = Sun
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

    const ZH_WEEKDAYS_SHORT = ['日', '一', '二', '三', '四', '五', '六'];
    const EN_WEEKDAYS_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const weekdayLabels = lang === 'zh' ? ZH_WEEKDAYS_SHORT : EN_WEEKDAYS_SHORT;

    const monthLabel = lang === 'zh'
        ? `${year}年${monthIndex + 1}月`
        : month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const goPrevMonth = () => onMonthChange(new Date(year, monthIndex - 1, 1));
    const goNextMonth = () => onMonthChange(new Date(year, monthIndex + 1, 1));

    // Build a flat list of cells: leading blanks + actual days of the month.
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) cells.push(day);

    const selectedKey = selectedDay
        ? `${selectedDay.getFullYear()}-${selectedDay.getMonth()}-${selectedDay.getDate()}`
        : null;
    const selectedDayEvents = selectedKey ? (eventsByDay[selectedKey] || []) : [];

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-3 px-1">
                <button
                    onClick={goPrevMonth}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
                    aria-label="Previous month"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <span className="font-bold text-gray-900 text-sm">{monthLabel}</span>
                <button
                    onClick={goNextMonth}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
                    aria-label="Next month"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {/* Weekday header row */}
            <div className="grid grid-cols-7 mb-1">
                {weekdayLabels.map((w, i) => (
                    <div key={i} className="text-center text-xs font-semibold text-gray-400 py-1">{w}</div>
                ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 gap-1.5">
                {cells.map((day, idx) => {
                    if (day === null) {
                        return <div key={`blank-${idx}`} />;
                    }
                    const dayDate = new Date(year, monthIndex, day);
                    dayDate.setHours(0, 0, 0, 0);
                    const key = `${year}-${monthIndex}-${day}`;
                    const dayEvents = eventsByDay[key] || [];
                    const hasEvents = dayEvents.length > 0;
                    const isToday = dayDate.getTime() === today.getTime();
                    const isPast = dayDate.getTime() < today.getTime();
                    const isSelected = key === selectedKey;

                    return (
                        <button
                            key={key}
                            disabled={!hasEvents}
                            onClick={() => hasEvents && onSelectDay(dayDate)}
                            className={`relative aspect-square min-h-[48px] rounded-xl flex flex-col items-center justify-center transition-all ${
                                hasEvents
                                    ? isPast
                                        ? "bg-orange-50 border border-orange-100 text-gray-700 hover:bg-orange-100"
                                        : "bg-orange-50 border border-orange-200 text-gray-900 hover:bg-orange-100 cursor-pointer"
                                    : "text-gray-300 cursor-default"
                            } ${isToday && !isSelected ? "ring-2 ring-orange-300" : ""} ${
                                isSelected ? "ring-2 ring-orange-500 bg-orange-100" : ""
                            }`}
                        >
                            <span className={`text-sm ${hasEvents ? "font-semibold" : "font-medium"}`}>{day}</span>
                            {hasEvents && (
                                <span className="flex gap-0.5 mt-1">
                                    {dayEvents.slice(0, 3).map((_, i) => (
                                        <span key={i} className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                                    ))}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Selected day's events, shown inline below the grid — same full
                card as the list view (badges, facepile, spots left, admin actions). */}
            {selectedDay && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
                        {t('home.eventsOnDay')} {selectedDay.toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', { month: 'short', day: 'numeric' })}
                    </p>
                    {selectedDayEvents.length === 0 ? (
                        <p className="text-sm text-gray-400 px-1 py-3">{t('home.noEventsThisDay')}</p>
                    ) : (
                        <div className="space-y-3">
                            {selectedDayEvents.map((event) => (
                                <GravityCard key={event.id}>
                                    <EventCard
                                        event={event}
                                        venues={venues}
                                        avatarMap={avatarMap}
                                        currentUser={currentUser}
                                        isAdmin={isAdmin}
                                        t={t}
                                        getEventRegistration={getEventRegistration}
                                        getEventStatus={getEventStatus}
                                        onCardClick={onEventClick}
                                        handleMarkAsFull={handleMarkAsFull}
                                        handleEditEvent={handleEditEvent}
                                        handleDuplicateEvent={handleDuplicateEvent}
                                        handleDeleteEvent={handleDeleteEvent}
                                        handleShare={handleShare}
                                        handleToggleHideEvent={handleToggleHideEvent}
                                        duplicatingEvent={duplicatingEvent}
                                        hasDanglingPayment={danglingPaymentEventIds.has(event.id)}
                                        activeChallenges={activeChallengesMap[event.id] || []}
                                        lang={lang}
                                        pendingPromotion={pendingPromotionsMap[event.id] || null}
                                    />
                                </GravityCard>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Home() {
    const [events, setEvents] = useState([]);
    const [pastEvents, setPastEvents] = useState([]);
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingPastEvents, setLoadingPastEvents] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [showShareModal, setShowShareModal] = useState(false);
    const [selectedEventForShare, setSelectedEventForShare] = useState(null);

    // Active challenges per event — { eventId: [{ id, type, teamA, teamB, ... }] }
    const [activeChallengesMap, setActiveChallengesMap] = useState({});
    
    // Week view states (replacing full calendar)
    const [selectedDate, setSelectedDate] = useState(new Date());
    // "list" = the existing date-strip + card feed. "calendar" = month grid view.
    // Persisted to localStorage so the user's chosen view survives page reloads.
    const DISPLAY_MODE_KEY = 'gtamelb_home_display_mode';
    const [displayMode, setDisplayMode] = useState(() => {
        try {
            const saved = localStorage.getItem(DISPLAY_MODE_KEY);
            return saved === 'calendar' || saved === 'list' ? saved : 'list';
        } catch { return 'list'; }
    });
    useEffect(() => {
        try { localStorage.setItem(DISPLAY_MODE_KEY, displayMode); } catch { /* private mode — best effort */ }
    }, [displayMode]);
    // Independent month cursor for the calendar grid — kept separate from
    // selectedPeriod (used by the unrelated admin Past Events modal) so the
    // two views never interfere with each other.
    const [calendarMonth, setCalendarMonth] = useState(() => {
        const d = new Date();
        d.setDate(1);
        d.setHours(0, 0, 0, 0);
        return d;
    });
    // Day tapped inside the calendar grid — its events render inline below
    // the grid, without leaving calendar mode or touching the list view's
    // own selectedDate/viewMode.
    const [calendarSelectedDay, setCalendarSelectedDay] = useState(null);
    const [viewMode, setViewMode] = useState("upcoming"); // "upcoming", "all", "day"

    // Admin past events functionality
    const [showPastEvents, setShowPastEvents] = useState(false);
    const [dateFilter, setDateFilter] = useState("month");
    const [selectedPeriod, setSelectedPeriod] = useState(new Date());

    // Admin functionality states
    const [showEditModal, setShowEditModal] = useState(false);
    const [waitlistPromoteModal, setWaitlistPromoteModal] = useState(null); // { eventId, eventTitle, toPromote, waitlist, oldMax, newMax, isPaid }
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [savingEvent, setSavingEvent] = useState(false);
    const [deletingEvent, setDeletingEvent] = useState(false);
    const [duplicatingEvent, setDuplicatingEvent] = useState(null);
    const [availableCourts, setAvailableCourts] = useState([]);
    const [signupOptions, setSignupOptions] = useState([{ name: "", price: 0, allowQuantity: false, maxQuantity: 10, requirePlayerNames: false, requireNotes: false, required: false, requiredGroup: "", isPlayerTicket: false }]);
    // Add-on events: other events the admin links so players can optionally
    // pay for them together.  Each entry: { eventId, label, price }
    const [addonEvents, setAddonEvents] = useState([]);
    const [showAdminMenu, setShowAdminMenu] = useState(false);
    const [showShareAllModal, setShowShareAllModal] = useState(false);
    const [shareAllLanguage, setShareAllLanguage] = useState('en'); // 'en' | 'zh'
    const [shareAllText, setShareAllText] = useState('');

    const { currentUser, userProfile, isAdmin } = useAuth();
    const { t, lang } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    // ?edit=<eventId> deep link (from the event page's admin tools) — handled once per id.
    const editDeepLinkHandledRef = useRef(null);
    const [registrations, setRegistrations] = useState([]);
    // Event IDs that have a dangling paymentAttempts doc (status "started")
    // for the current user — used to show a "?" badge on those event cards.
    const [danglingPaymentEventIds, setDanglingPaymentEventIds] = useState(new Set());
    // eventId -> registration with paymentStatus "promotion_pending" for this user
    const [pendingPromotionsMap, setPendingPromotionsMap] = useState({});
    // uid -> { photoURL, name } for participant facepiles on event cards
    const [avatarMap, setAvatarMap] = useState({});

    // Measures the fixed header's real height so the content below it gets
    // exactly the right top padding — no hardcoded px guess that goes stale
    // whenever the header's content (date strip, filters, language) changes.
    const fixedHeaderRef = useRef(null);
    const [fixedHeaderHeight, setFixedHeaderHeight] = useState(0);
    useEffect(() => {
        const el = fixedHeaderRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver((entries) => {
            const h = entries[0]?.contentRect?.height;
            if (h != null) setFixedHeaderHeight(Math.ceil(h));
        });
        observer.observe(el);
        setFixedHeaderHeight(Math.ceil(el.getBoundingClientRect().height));
        return () => observer.disconnect();
    }, []);

    // Helper function to check if date is today
    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const isSelected = (date) => {
        return date.toDateString() === selectedDate.toDateString();
    };

    const initialLoadRef = React.useRef(false);
    // True only for the render right after the first data arrives — used to
    // gate the staggered fade-in so live Firestore updates later don't replay it.
    const justLoadedRef = useRef(false);
    // Has the events listener delivered its first snapshot yet? (ref, not state,
    // so the onSnapshot closure always reads the current value, not a stale one.)
    const hasLoadedOnceRef = useRef(false);
    // Tracks whether we've already told App.js that Home has data, so we
    // only dispatch the ready event once per mount.
    const homeReadyDispatchedRef = useRef(false);

    useEffect(() => {
        // Only reset loading state on the very first mount, not on currentUser change
        if (!initialLoadRef.current) {
            initialLoadRef.current = true;
            setLoading(true);

            // Tell App.js that Home has mounted and its skeleton is now
            // painted on screen — distinct from 'homeDataReady' below, which
            // only fires once real Firestore data has arrived. App.js uses
            // THIS event (not homeDataReady) to hide the static index.html
            // loading screen, since the skeleton is real visible content,
            // not a blank gap, so there's no reason to keep showing the
            // static loader past this point.
            window.__homeMounted = true;
            window.dispatchEvent(new CustomEvent('homeMounted'));
        }

        // Events + venues don't depend on the logged-in user, so subscribe ONCE.
        // (Previously this was in a [currentUser] effect, so it re-ran when auth
        // resolved undefined -> user, loading the home page twice.)
        const unsubscribeEvents = setupEventsListener();
        fetchVenues();

        return () => {
            if (unsubscribeEvents) unsubscribeEvents();
        };
    }, []);

    // Registrations DO depend on the user — subscribe/cleanup when currentUser changes.
    useEffect(() => {
        const unsubscribeRegistrations = currentUser?.uid ? setupRegistrationsListener() : null;
        return () => {
            if (unsubscribeRegistrations) unsubscribeRegistrations();
        };
    }, [currentUser]);

    useEffect(() => {
        if (showPastEvents && isAdmin) {
            fetchPastEvents();
        }
    }, [showPastEvents, selectedPeriod, dateFilter, isAdmin]);

    const setupEventsListener = () => {
        try {
            // Filter to upcoming events SERVER-side.
            //
            // This previously subscribed to the entire events collection and
            // discarded past events in JS. That payload grows forever and,
            // because it is an onSnapshot, the whole thing is re-delivered on
            // every write to any event document — the main reason the home
            // feed got slower every month and struggled on mobile.
            //
            // Past events are fetched separately by fetchPastEvents(), and only
            // when an admin opens that panel, so nothing is lost here.
            //
            // Requires a composite index on events: endDate ASC.
            // Events with no endDate will not match — backfill them once with
            // endDate = startDate + durationHours before deploying this.
            const nowISO = new Date().toISOString();
            const eventsQuery = query(
                collection(db, "events"),
                where("endDate", ">=", nowISO),
                orderBy("endDate", "asc"),
                limit(100)
            );

            return onSnapshot(
                eventsQuery,
                (querySnapshot) => {
                    const eventsList = [];

                    querySnapshot.forEach((doc) => {
                        eventsList.push({ id: doc.id, ...doc.data() });
                    });

                    // Restore the original display order (soonest start first).
                    // The query has to order by endDate because that is the field
                    // it ranges over; sorting by startDate is done here instead.
                    eventsList.sort(
                        (a, b) => new Date(a.startDate) - new Date(b.startDate)
                    );

                    // Only stagger-animate the transition out of the skeleton state,
                    // not every subsequent live update from Firestore.
                    justLoadedRef.current = !hasLoadedOnceRef.current;
                    hasLoadedOnceRef.current = true;

                    setEvents(eventsList);
                    setLoading(false);

                    // Tell App.js (and any other listener, e.g.
                    // LetterDropEffect.js) that Home has its first real
                    // data. Sets a plain window flag in addition to firing
                    // the event — a listener that mounts AFTER this event
                    // already fired (timing between components isn't
                    // guaranteed) would otherwise miss it entirely; checking
                    // the flag directly covers that race.
                    if (!homeReadyDispatchedRef.current) {
                        homeReadyDispatchedRef.current = true;
                        window.__homeDataReady = true;
                        window.dispatchEvent(new CustomEvent('homeDataReady'));
                    }
                },
                (error) => {
                    showNotification('Error loading events', 'error');
                    setLoading(false);
                    // Don't let an error block the loading screen forever.
                    if (!homeReadyDispatchedRef.current) {
                        homeReadyDispatchedRef.current = true;
                        window.__homeDataReady = true;
                        window.dispatchEvent(new CustomEvent('homeDataReady'));
                    }
                }
            );
        } catch (error) {
            console.error("Error setting up events listener:", error);
            setLoading(false);
            return null;
        }
    };

    // Fetch participant avatars for facepile.
    // Always reads photoURL from the users collection (source of truth)
    // so manually uploaded avatars (synced via avatarUtils) show immediately.
    // Registrations provide names only since userAvatar can be stale.

    // ── Live active challenges listener ────────────────────────────
    // Listens to each visible event's challenges subcollection for
    // active and pending challenges. Builds a map for EventCard fire
    // badges and tracks the current user's personal challenges.
    // Stable keys so these effects re-run only when the SET of events (or
    // their participant counts) changes — not on every live view-count tick,
    // which used to tear down every challenge listener and re-fetch every
    // event's registrations each time anyone opened an event page.
    const homeEventsRef = useRef(events);
    homeEventsRef.current = events;
    const eventIdsKey = (events || []).map((e) => e.id).join(',');
    const facepileKey = (events || []).map((e) => `${e.id}:${(e.participants || []).length}`).join(',');

    useEffect(() => {
        const events = homeEventsRef.current;
        if (!events || events.length === 0) {
            setActiveChallengesMap({});
            return;
        }

        const unsubs = [];

        events.forEach((ev) => {
            const q = query(
                collection(db, 'events', ev.id, 'challenges'),
                where('status', 'in', ['active', 'pending'])
            );
            const unsub = onSnapshot(q, (snap) => {
                const challenges = [];
                snap.forEach((d) => challenges.push({ id: d.id, eventId: ev.id, eventTitle: ev.title, ...d.data() }));
                setActiveChallengesMap((prev) => ({ ...prev, [ev.id]: challenges }));
            }, (err) => {
                console.error(`Challenge listener error for event ${ev.id}:`, err);
            });
            unsubs.push(unsub);
        });

        return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventIdsKey]);

    useEffect(() => {
        const events = homeEventsRef.current;
        if (!events || events.length === 0) return;
        const eventsToFetch = events.filter(ev => (ev.participants || []).length > 0);
        if (eventsToFetch.length === 0) return;

        let cancelled = false;
        (async () => {
            // Step 1: collect uid → name from registrations.
            //
            // Concurrency capped at four. This used to issue one query per
            // event on the feed simultaneously — with 40+ events visible that
            // is 40 parallel queries each returning every registration for its
            // event, all resolving into memory at once. Combined with step 2
            // below it was the heaviest thing the home page did, and a real
            // cause of mobile tabs being killed and reloaded.
            const nameMap = {};
            const uidsSeen = new Set();
            let feedCursor = 0;
            await Promise.all(
                Array.from({ length: Math.min(4, eventsToFetch.length) }, async () => {
                  while (feedCursor < eventsToFetch.length) {
                    const ev = eventsToFetch[feedCursor++];
                    try {
                        const regsSnap = await getDocs(query(collection(db, "registrations"), where("eventId", "==", ev.id)));
                        regsSnap.forEach(d => {
                            const reg = d.data();
                            if (reg.userId && reg.userName && reg.status !== 'cancelled') {
                                nameMap[reg.userId] = reg.userName;
                                uidsSeen.add(reg.userId);
                            }
                        });
                    } catch (e) { console.error('[Facepile] fetch error:', e); }
                  }
                })
            );
            if (cancelled) return;

            // Step 2: avatars, batched ten per query and cached across the
            // session. This was one getDoc per user with no cap and no cache —
            // hundreds of simultaneous reads, repeated on every remount, for
            // pictures that essentially never change.
            const updates = await fetchUserAvatars([...uidsSeen], nameMap);

            if (!cancelled && Object.keys(updates).length > 0) {
                setAvatarMap((prev) => ({ ...prev, ...updates }));
            }
        })();

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facepileKey]);

    const setupRegistrationsListener = () => {
        try {
            const regsQuery = query(
                collection(db, "registrations"),
                where("userId", "==", currentUser.uid)
            );

            return onSnapshot(regsQuery, (snapshot) => {
                const regs = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setRegistrations(regs);
            });
        } catch (error) {
            console.error("Error setting up registrations listener:", error);
            return null;
        }
    };

    const fetchVenues = async () => {
        try {
            // Check sessionStorage cache first — venues rarely change
            const cached = sessionStorage.getItem('gtamelb_venues');
            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    if (parsed.length > 0 && Date.now() - (parsed._ts || 0) < 30 * 60 * 1000) { // 30 min cache
                        setVenues(parsed.data || parsed);
                        return;
                    }
                } catch {}
            }
            const venuesQuery = query(collection(db, "venues"));
            const querySnapshot = await getDocs(venuesQuery);
            const venuesList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setVenues(venuesList);
            try { sessionStorage.setItem('gtamelb_venues', JSON.stringify({ data: venuesList, _ts: Date.now() })); } catch {}
        } catch (error) {
            console.error("Error fetching venues:", error);
        }
    };

    // Real-time listener for dangling payment attempts — shows "?" badge
    // on event cards. Uses onSnapshot so dismissals on EventDetail page
    // (or 5-min auto-dismiss) sync immediately without needing navigation.
    useEffect(() => {
        if (!currentUser) { setDanglingPaymentEventIds(new Set()); return; }
        const q = query(
            collection(db, 'paymentAttempts'),
            where('userId', '==', currentUser.uid),
            where('status', '==', 'started')
        );
        const unsubscribe = onSnapshot(q, (snap) => {
            const ids = new Set();
            snap.docs.forEach(d => {
                const data = d.data();
                if (data.eventId) {
                    const hasConfirmed = registrations.some(r =>
                        r.eventId === data.eventId &&
                        r.paymentStatus === 'confirmed' &&
                        r.status !== 'cancelled'
                    );
                    if (!hasConfirmed) ids.add(data.eventId);
                }
            });
            setDanglingPaymentEventIds(ids);
        }, (err) => {
            console.error('[Home] dangling payment listener error:', err);
        });
        return () => unsubscribe();
    }, [currentUser, registrations]);

    // Single listener for this user's pending waitlist promotions (two equality
    // filters → no composite index needed). Feeds the countdown box on cards.
    useEffect(() => {
        if (!currentUser?.uid) { setPendingPromotionsMap({}); return undefined; }
        const q = query(
            collection(db, 'registrations'),
            where('userId', '==', currentUser.uid),
            where('paymentStatus', '==', 'promotion_pending')
        );
        const unsubscribe = onSnapshot(q, (snap) => {
            const map = {};
            snap.docs.forEach((d) => {
                const r = { id: d.id, ...d.data() };
                if (r.eventId && r.status !== 'cancelled') map[r.eventId] = r;
            });
            setPendingPromotionsMap(map);
        }, (err) => {
            console.error('[Home] promotion listener error:', err);
        });
        return () => unsubscribe();
    }, [currentUser?.uid]);

    const [pastEventRegStats, setPastEventRegStats] = useState({ uniqueUsers: 0, totalGuests: 0 });
    const [pastEventRegTimes, setPastEventRegTimes] = useState({}); // { eventId: [isoString, ...] }

    const fetchPastEvents = async () => {
        if (!isAdmin) return;

        setLoadingPastEvents(true);
        try {
            const now = new Date();
            let startDate, endDate;

            if (dateFilter === "month") {
                startDate = new Date(selectedPeriod.getFullYear(), selectedPeriod.getMonth(), 1);
                endDate = new Date(selectedPeriod.getFullYear(), selectedPeriod.getMonth() + 1, 0, 23, 59, 59);
            } else {
                const currentDate = new Date(selectedPeriod);
                const dayOfWeek = currentDate.getDay();
                startDate = new Date(currentDate);
                startDate.setDate(currentDate.getDate() - dayOfWeek);
                startDate.setHours(0, 0, 0, 0);

                endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + 6);
                endDate.setHours(23, 59, 59, 999);
            }

            // Server-side filter: only fetch events within the date range
            // Uses endDate for filtering since we want events that ended in this period
            const pastEventsQuery = query(
                collection(db, "events"),
                where("endDate", ">=", startDate.toISOString()),
                where("endDate", "<=", now.toISOString()),
                orderBy("endDate", "desc")
            );
            const querySnapshot = await getDocs(pastEventsQuery);
            const allEventsList = [];

            querySnapshot.forEach((doc) => {
                const eventData = { id: doc.id, ...doc.data() };
                const eventEndDate = new Date(eventData.endDate);
                // Only include events within the selected period (month/week)
                if (eventEndDate <= endDate) {
                    allEventsList.push(eventData);
                }
            });

            setPastEvents(allEventsList);

            // Fetch registration stats for unique users + guest counts + timelines
            if (allEventsList.length > 0) {
                const eventIds = allEventsList.map(e => e.id);
                const uniqueUserIds = new Set();
                let guestCount = 0;
                const timelineMap = {}; // eventId -> [{registeredAt, ...}]

                // Query registrations in batches of 10 (Firestore 'in' limit)
                for (let i = 0; i < eventIds.length; i += 10) {
                    const batch = eventIds.slice(i, i + 10);
                    const regsQuery = query(
                        collection(db, "registrations"),
                        where("eventId", "in", batch),
                        where("status", "!=", "cancelled")
                    );
                    const regsSnap = await getDocs(regsQuery);
                    regsSnap.forEach(d => {
                        const reg = d.data();
                        uniqueUserIds.add(reg.userId);
                        // Count guests from player ticket options
                        if (reg.selectedOptions) {
                            const evt = allEventsList.find(e => e.id === reg.eventId);
                            if (evt?.signupOptions) {
                                reg.selectedOptions.forEach(so => {
                                    if (so && typeof so === 'object') {
                                        const opt = evt.signupOptions[so.index];
                                        if (opt?.isPlayerTicket) {
                                            guestCount += (so.quantity || 1);
                                        }
                                    }
                                });
                            }
                        }
                        // Collect registration timestamps for timeline
                        if (reg.eventId && reg.registeredAt) {
                            if (!timelineMap[reg.eventId]) timelineMap[reg.eventId] = [];
                            let regTime = reg.registeredAt;
                            // Handle Firestore Timestamp objects
                            if (regTime && typeof regTime.toDate === 'function') regTime = regTime.toDate().toISOString();
                            else if (regTime && regTime._seconds) regTime = new Date(regTime._seconds * 1000).toISOString();
                            timelineMap[reg.eventId].push(regTime);
                        }
                    });
                }
                setPastEventRegStats({ uniqueUsers: uniqueUserIds.size, totalGuests: guestCount });

                // Store raw registration timestamps per event for timeline charts
                setPastEventRegTimes(timelineMap);
            } else {
                setPastEventRegStats({ uniqueUsers: 0, totalGuests: 0 });
                setPastEventRegTimes({});
            }
        } catch (error) {
            console.error("Error fetching past events:", error);
            showNotification('Error loading past events', 'error');
        } finally {
            setLoadingPastEvents(false);
        }
    };

    // Filter events based on selected date, view mode, and venue name
    const getFilteredEvents = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let filtered = events;

        // Hide events with hiddenFromList flag for non-admin users
        if (!isAdmin) {
            filtered = filtered.filter(event => !event.hiddenFromList);
        }

        // Filter by venue name if search term is set
        if (searchTerm) {
            filtered = filtered.filter(event => {
                const eventVenue = venues.find(v => v.id === event.venueId);
                const eventVenueName = eventVenue?.name || event.venueName;
                return eventVenueName === searchTerm;
            });
        }

        // Filter by date/view mode
        if (viewMode === "upcoming") {
            // Show only future events (default view)
            return filtered.filter(event => {
                const eventDate = new Date(event.startDate);
                eventDate.setHours(0, 0, 0, 0);
                return eventDate >= today;
            });
        } else if (viewMode === "all") {
            // Show all events (no date filter)
            return filtered;
        } else if (viewMode === "day") {
            return filtered.filter(event => {
                const eventDate = new Date(event.startDate);
                eventDate.setHours(0, 0, 0, 0);
                const selDate = new Date(selectedDate);
                selDate.setHours(0, 0, 0, 0);
                return eventDate.getTime() === selDate.getTime();
            });
        }
        return filtered;
    };

    // Events for the calendar grid: respects the venue/search filter chips,
    // but always shows the whole month (never narrowed to a single selected
    // day the way getFilteredEvents() is when viewMode === "day").
    const getCalendarEvents = () => {
        let filtered = events;
        // Hide events with hiddenFromList flag for non-admin users
        if (!isAdmin) {
            filtered = filtered.filter(event => !event.hiddenFromList);
        }
        if (searchTerm) {
            filtered = filtered.filter(event => {
                const eventVenue = venues.find(v => v.id === event.venueId);
                const eventVenueName = eventVenue?.name || event.venueName;
                return eventVenueName === searchTerm;
            });
        }
        return filtered;
    };

    // Group events by date
    const groupEventsByDate = (eventsList) => {
        const grouped = {};
        eventsList.forEach(event => {
            const eventDate = new Date(event.startDate);
            // Stable EN grouping key — display label is computed separately at render
            // time so it can switch language without re-grouping events.
            const weekday = eventDate.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
            const month = eventDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
            const day = eventDate.getDate();
            const year = eventDate.getFullYear();
            const dateKey = `${weekday}, ${month} ${day}, ${year}`;
            
            if (!grouped[dateKey]) {
                grouped[dateKey] = [];
            }
            grouped[dateKey].push(event);
        });
        return grouped;
    };

    // Maps a "YYYY-M-D" key -> events on that local calendar day. Used by the
    // calendar/timetable grid so each cell can show its own event count and
    // lookup is O(1) instead of scanning the full events array per cell.
    const buildEventsByDayKey = (eventsList) => {
        const map = {};
        eventsList.forEach(event => {
            const d = new Date(event.startDate);
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            if (!map[key]) map[key] = [];
            map[key].push(event);
        });
        return map;
    };

    // Language-aware date header label, e.g. "TUESDAY, JUN 23, 2026" (en)
    // or "周二 6月23日，2026" (zh). Pass any event's startDate from the group.
    const ZH_WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    const formatDateHeader = (dateLike) => {
        const d = new Date(dateLike);
        if (lang === 'zh') {
            return `${ZH_WEEKDAYS[d.getDay()]} ${d.getMonth() + 1}月${d.getDate()}日`;
        }
        const weekday = d.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
        const month = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
        return `${weekday}, ${month} ${d.getDate()}, ${d.getFullYear()}`;
    };

    const showNotification = (message, type = "info") => {
        const notification = document.createElement("div");
        notification.className = `fixed top-4 left-1/2 -translate-x-1/2 z-50 p-3 rounded-lg border w-[60%] ${
            type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : type === "error"
                ? "bg-red-50 border-red-200 text-red-800"
                : type === "warning"
                ? "bg-yellow-50 border-yellow-200 text-yellow-800"
                : "bg-blue-50 border-blue-200 text-blue-800"
        }`;
        notification.innerHTML = `
      <div class="flex items-center text-sm">
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          ${
              type === "success"
                  ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>'
                  : type === "error"
                  ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>'
                  : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>'
          }
        </svg>
        <span>${message}</span>
      </div>
    `;
        document.body.appendChild(notification);

        setTimeout(() => {
            notification.remove();
        }, 4000);
    };

    const isoToDateTimeLocal = (isoString) => {
        if (!isoString) return "";
        
        try {
            const date = new Date(isoString);
            
            if (isNaN(date.getTime())) {
                console.error('Invalid ISO date string:', isoString);
                return "";
            }

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (error) {
            console.error('Error converting ISO to datetime-local:', error);
            return "";
        }
    };

    const dateTimeLocalToISO = (datetimeLocal) => {
        if (!datetimeLocal) return "";
        
        try {
            const date = new Date(datetimeLocal);
            
            if (isNaN(date.getTime())) {
                console.error('Invalid datetime-local string:', datetimeLocal);
                return "";
            }
            
            return date.toISOString();
        } catch (error) {
            console.error('Error converting datetime-local to ISO:', error);
            return "";
        }
    };

    const getEventRegistration = (eventId) => {
        return registrations.find(reg => 
            reg.eventId === eventId && 
            reg.status !== "cancelled"
        ) || null;
    };

    const getEventStatus = (event) => {
        const now = new Date();
        const eventStart = new Date(event.startDate);
        const eventEnd = new Date(event.endDate);
        
        // Event has finished
        if (now >= eventEnd) return 'closed';
        
        // Event is currently happening (now is after start and before end)
        if (now >= eventStart && now < eventEnd) return 'ongoing';
        
        // Event hasn't started yet but is full.
        // Via getEventCounts so a negative reservedForPromotion (the data bug
        // behind the wrong-waitlist-promotion issue) can't make a full event
        // report itself as open.
        if (getEventCounts(event).isFull) return 'full';
        
        // Event hasn't started and has spots available
        return 'open';
    };

    const handleEventClick = async (eventId) => {
        navigate(`/event/${eventId}`);
    };

    const handleQuickShare = (event, method, e) => {
        e.stopPropagation();

        const registrationCount = event.currentParticipants || 0;

        switch (method) {
            case "whatsapp":
                shareToWhatsApp(event, null, registrationCount);
                break;
            case "facebook":
                shareToFacebook(event, null, registrationCount);
                break;
            case "copy":
                copyEventToClipboard(event, null, registrationCount)
                    .then(() => showNotification("Event details copied to clipboard!", "success"))
                    .catch(() => showNotification("Failed to copy event details", "error"));
                break;
            case "more":
                setSelectedEventForShare(event);
                setShowShareModal(true);
                break;
        }
    };

    const handleShare = (event, e) => {
        if (e) e.stopPropagation();
        setSelectedEventForShare(event);
        setShowShareModal(true);
    };

    // Shared time formatter for share messages
    const formatShareTime = (date) => {
        const h = date.getHours();
        const m = date.getMinutes();
        const displayH = h > 12 ? h - 12 : (h === 0 ? 12 : h);
        const displayM = m === 0 ? '' : `.${String(m).padStart(2, '0')}`;
        return `${displayH}${displayM}`;
    };

    const buildShareMessage = (lang) => {
        const upcomingEvents = getFilteredEvents();
        if (upcomingEvents.length === 0) return '';

        const CN_DAYS = { 0: '周日', 1: '周一', 2: '周二', 3: '周三', 4: '周四', 5: '周五', 6: '周六' };

        const lines = upcomingEvents.map(event => {
            const start = new Date(event.startDate);
            const end = new Date(event.endDate);

            const month = start.getMonth() + 1;
            const day = start.getDate();

            const startStr = formatShareTime(start);
            const endStr = formatShareTime(end);

            const startIsPM = start.getHours() >= 12;
            const endIsPM = end.getHours() >= 12;
            const timeSuffix = startIsPM === endIsPM
                ? `${startStr}-${endStr}${endIsPM ? 'pm' : 'am'}`
                : `${startStr}${startIsPM ? 'pm' : 'am'}-${endStr}${endIsPM ? 'pm' : 'am'}`;

            const venueName = venues.find(v => v.id === event.venueId)?.name || event.venueName || '';
            const venueAbbr = venueName
                ? venueName.split(' ').map(w => w[0]).join('').toUpperCase()
                : '';

            // Duration label — omit if exactly 2h (standard)
            const durationMs = end - start;
            const durationH = durationMs / (1000 * 60 * 60);
            const durationLabel = durationH !== 2 ? ` ${durationH % 1 !== 0 ? durationH : Math.round(durationH)}h` : '';

            // Inline note from event description (first line only, if short)
            const inlineNote = event.shareNote || '';

            if (lang === 'zh') {
                const dayNameZH = CN_DAYS[start.getDay()];
                return `${month}.${day} ${dayNameZH} ${timeSuffix} ${venueAbbr}${durationLabel}${inlineNote ? ' ' + inlineNote : ''}`.trim();
            } else {
                const dayNameEN = start.toLocaleDateString('en-US', { weekday: 'short' });
                return `${month}.${day} ${dayNameEN} ${timeSuffix} ${venueAbbr}${durationLabel}${inlineNote ? ' ' + inlineNote : ''}`.trim();
            }
        });

        if (lang === 'zh') {
            return `@所有人 下周活动汇总\n${lines.join('\n')}\n\n建议收藏小程序页面，方便查看活动哦🥰请大家一定注意活动时间，有问题随时联系我，谢谢各位\n#小程序://羽球助手/PHDcr6TQJgeEG1w`;
        } else {
            return `@all\n${lines.join('\n')}\n\nPlease register in advance on our website and read the event details. (Please note that walk-ins are not accepted.)\nhttps://gtamelb.com/`;
        }
    };

    const handleShareAllUpcoming = () => {
        setShowAdminMenu(false);
        const upcomingEvents = getFilteredEvents();
        if (upcomingEvents.length === 0) {
            showNotification('No upcoming events to share', 'warning');
            return;
        }
        const text = buildShareMessage(shareAllLanguage);
        setShareAllText(text);
        setShowShareAllModal(true);
    };

    const handleShareAllLanguageSwitch = (lang) => {
        setShareAllLanguage(lang);
        setShareAllText(buildShareMessage(lang));
    };

    const handleCopyShareAll = async () => {
        try {
            await navigator.clipboard.writeText(shareAllText);
            showNotification('Copied! Ready to paste 🏸', 'success');
        } catch {
            const ta = document.createElement('textarea');
            ta.value = shareAllText;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            showNotification('Copied! Ready to paste 🏸', 'success');
        }
    };

    // Admin Functions
    const handleEditEvent = (event, e) => {
        if (e) e.stopPropagation();
        
        try {
            setSelectedEvent(event);

            setEditFormData({
                title: event.title || "",
                description: event.description || "",
                organizer: event.organizer || "",
                price: event.price ? event.price.toString() : "0",
                maxParticipants: event.maxParticipants || 8,
                startDate: event.startDate ? isoToDateTimeLocal(event.startDate) : "",
                endDate: event.endDate ? isoToDateTimeLocal(event.endDate) : "",
                registrationDeadline: event.registrationDeadline ? isoToDateTimeLocal(event.registrationDeadline) : "",
                venueId: event.venueId || "",
                venueName: event.venueName || "",
                selectedCourts: Array.isArray(event.selectedCourts) ? event.selectedCourts : [],
                courtAssignment: event.courtAssignment || "",
                enableServices: event.enableServices !== undefined ? event.enableServices : true,
                cardStyle: event.cardStyle || { borderDash: false, borderColor: '#f97316', marqueeText: '', marqueeSpeed: 'normal' },
                multiEventDiscount: event.multiEventDiscount || { enabled: false, type: 'flat', amount: '', scope: 'sameDayVenue', label: '' },
                earlyBird: event.earlyBird || { enabled: false, price: '', cutoffHours: 24 },
                isFreeEvent: event.isFreeEvent === true,
                freeLabel: event.freeLabel || '',
                hideFreeLabel: event.hideFreeLabel === true,
                groups: eventGroups(event).map((g) => ({ ...g, courts: g.courts || [], price: groupHasPrice(g) ? g.price : null })),
            });

            // Load add-on events
            if (event.addonEvents && Array.isArray(event.addonEvents) && event.addonEvents.length > 0) {
                setAddonEvents(event.addonEvents.map(ae => ({
                    eventId: ae.eventId || "",
                    label: ae.label || "",
                    price: ae.price !== undefined ? ae.price.toString() : "0",
                })));
            } else {
                setAddonEvents([]);
            }

            if (event.signupOptions && Array.isArray(event.signupOptions) && event.signupOptions.length > 0) {
                setSignupOptions(
                    event.signupOptions.map((option) => ({
                        name: option.name || "",
                        price: option.price ? option.price.toString() : "0",
                        allowQuantity: option.allowQuantity || false,
                        maxQuantity: option.maxQuantity ? option.maxQuantity.toString() : "10",
                        requirePlayerNames: option.requirePlayerNames || false,
                        requireNotes: option.requireNotes || false,
                        required: option.required || false,
                        requiredGroup: option.requiredGroup || "",
                        isPlayerTicket: option.isPlayerTicket || false
                    }))
                );
            } else {
                setSignupOptions([{ name: "", price: "0", allowQuantity: false, maxQuantity: "10", requirePlayerNames: false, requireNotes: false, required: false, requiredGroup: "", isPlayerTicket: false }]);
            }

            if (event.venueId && venues.length > 0) {
                const selectedVenue = venues.find((v) => v.id === event.venueId);
                if (selectedVenue && Array.isArray(selectedVenue.courts)) {
                    setAvailableCourts(selectedVenue.courts);
                } else {
                    setAvailableCourts([]);
                }
            } else {
                setAvailableCourts([]);
            }

            setShowEditModal(true);
        } catch (error) {
            console.error('Error setting up edit form:', error);
            showNotification('Error opening edit form', 'error');
        }
    };

    // ?edit=<eventId>: once events (and venues, for the court picker) are
    // loaded, open the existing edit modal for that event — fetching the doc
    // directly when it is not in the loaded (upcoming) list — then drop the
    // param so a refresh / back doesn't reopen it.
    useEffect(() => {
        const editId = new URLSearchParams(location.search).get('edit');
        if (!editId || !isAdmin || loading) return;
        if (editDeepLinkHandledRef.current === editId) return;
        let cancelled = false;
        const clearParam = () => {
            const params = new URLSearchParams(location.search);
            params.delete('edit');
            const qs = params.toString();
            navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
        };
        const open = async () => {
            editDeepLinkHandledRef.current = editId;
            let target = events.find((ev) => ev.id === editId);
            if (!target) {
                try {
                    const snap = await getDoc(doc(db, 'events', editId));
                    if (snap.exists()) target = { id: snap.id, ...snap.data() };
                } catch (err) {
                    console.error('Error loading event for ?edit=:', err);
                }
            }
            if (cancelled) return;
            if (target) handleEditEvent(target);
            else showNotification(lang === 'zh' ? '找不到该活动' : 'Event not found', 'error');
            clearParam();
        };
        // Give venues a moment to load so the court list is populated;
        // proceed anyway if there are none.
        const timer = setTimeout(open, venues.length > 0 ? 0 : 1500);
        return () => { cancelled = true; clearTimeout(timer); if (editDeepLinkHandledRef.current === editId) editDeepLinkHandledRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, isAdmin, loading, events.length, venues.length]);

    const handleVenueChange = (e) => {
        const selectedVenue = venues.find((v) => v.id === e.target.value);
        setEditFormData({
            ...editFormData,
            venueId: e.target.value,
            venueName: selectedVenue ? selectedVenue.name : "",
            selectedCourts: [],
        });

        if (selectedVenue && selectedVenue.courts) {
            setAvailableCourts(selectedVenue.courts);
        } else {
            setAvailableCourts([]);
        }
    };

    const handleCourtSelection = (court) => {
        const isSelected = editFormData.selectedCourts.includes(court);
        if (isSelected) {
            setEditFormData({
                ...editFormData,
                selectedCourts: editFormData.selectedCourts.filter((c) => c !== court),
            });
        } else {
            setEditFormData({
                ...editFormData,
                selectedCourts: [...editFormData.selectedCourts, court],
            });
        }
    };

    const handleSignupOptionChange = (index, field, value) => {
        const updatedOptions = [...signupOptions];
        
        if (field === "price") {
            updatedOptions[index] = {
                ...updatedOptions[index],
                [field]: value === "" ? "" : value
            };
        } else {
            updatedOptions[index] = {
                ...updatedOptions[index],
                [field]: value
            };
        }
        
        setSignupOptions(updatedOptions);
    };

    const addSignupOption = () => {
        setSignupOptions([...signupOptions, { name: "", price: "0", allowQuantity: false, maxQuantity: "10", requirePlayerNames: false, requireNotes: false, required: false, requiredGroup: "", isPlayerTicket: false }]);
    };

    // "Add another player" ticket — defaults to the event base price but can be
    // adjusted independently. Player name(s) are collected at checkout.
    const addPlayerTicketOption = () => {
        setSignupOptions([...signupOptions, {
            name: "Additional Player",
            price: (editFormData.price ?? "0").toString(),
            allowQuantity: true,
            maxQuantity: "5",
            requirePlayerNames: true,
            requireNotes: false,
            required: false,
            requiredGroup: "",
            isPlayerTicket: true
        }]);
    };

    const removeSignupOption = (index) => {
        if (signupOptions.length > 1) {
            setSignupOptions(signupOptions.filter((_, i) => i !== index));
        }
    };

    const handleSaveEvent = async () => {
        if (!selectedEvent) return;
        
        setSavingEvent(true);
        try {
            if (!editFormData.title || !editFormData.startDate || !editFormData.endDate) {
                showNotification('Please fill in all required fields', 'error');
                return;
            }

            const startDate = new Date(editFormData.startDate);
            const endDate = new Date(editFormData.endDate);
            
            if (endDate <= startDate) {
                showNotification('End date must be after start date', 'error');
                return;
            }

            const basePrice = editFormData.price === "" ? 0 : (parseFloat(editFormData.price) || 0);
            const validSignupOptions = signupOptions
                .filter(option => option.name.trim() !== '')
                .map(option => {
                    const isPlayerTicket = option.isPlayerTicket || false;
                    return {
                        name: option.name.trim(),
                        price: editFormData.isFreeEvent === true ? 0 : (option.price === "" ? 0 : parseFloat(option.price) || 0),
                        allowQuantity: isPlayerTicket ? true : (option.allowQuantity || false),
                        maxQuantity: (isPlayerTicket || option.allowQuantity)
                            ? (parseInt(option.maxQuantity, 10) || (isPlayerTicket ? 5 : 10))
                            : null,
                        requirePlayerNames: isPlayerTicket ? true : (option.requirePlayerNames || false),
                        requireNotes: option.requireNotes || false,
                        required: option.required || false,
                        requiredGroup: isPlayerTicket ? "" : ((option.requiredGroup || "").trim()),
                        isPlayerTicket
                    };
                });

            const price = editFormData.price === "" ? 0 : parseFloat(editFormData.price);
            const groupsError = validateGroups(editFormData.groups || []);
            if (groupsError) {
                showNotification(groupsError, 'error');
                return;
            }
            const cleanGroups = normalizeGroups(editFormData.groups || [], { isFreeEvent: editFormData.isFreeEvent === true });
            const maxParticipants = cleanGroups.length
                ? totalGroupCapacity(cleanGroups)
                : (editFormData.maxParticipants === "" ? 8 : parseInt(editFormData.maxParticipants, 10));

            if (isNaN(price) || price < 0) {
                showNotification('Please enter a valid price', 'error');
                return;
            }
            
            if (isNaN(maxParticipants) || maxParticipants < 1) {
                showNotification('Please enter a valid number of participants (minimum 1)', 'error');
                return;
            }

            const roundedPrice = Math.round(price * 100) / 100;

            const startDateISO = dateTimeLocalToISO(editFormData.startDate);
            const registrationDeadlineISO = new Date(new Date(startDateISO).getTime() - (60 * 60 * 1000)).toISOString();

            const updateData = {
                title: editFormData.title.trim(),
                description: editFormData.description?.trim() || '',
                organizer: editFormData.organizer?.trim() || '',
                price: editFormData.isFreeEvent === true ? 0 : roundedPrice,
                maxParticipants: maxParticipants,
                groups: cleanGroups,
                // A group that gained seats while the total stayed the same
                // (seats moved between groups) changes no event-wide counter,
                // so nudge the waitlist trigger explicitly.
                ...(() => {
                    const before = new Map(eventGroups(selectedEvent).map((g) => [g.id, parseInt(g.capacity, 10) || 0]));
                    const gained = cleanGroups.reduce((s, g) => s + Math.max(0, g.capacity - (before.get(g.id) ?? g.capacity)), 0);
                    return gained > 0 ? { _groupMoveNonce: Date.now(), _groupMoveSpots: gained } : {};
                })(),
                startDate: startDateISO,
                endDate: dateTimeLocalToISO(editFormData.endDate),
                registrationDeadline: registrationDeadlineISO,
                venueId: editFormData.venueId || '',
                venueName: editFormData.venueName || '',
                selectedCourts: editFormData.selectedCourts || [],
                courtAssignment: (editFormData.selectedCourts || []).join(', '),
                signupOptions: validSignupOptions,
                enableServices: editFormData.enableServices !== undefined ? editFormData.enableServices : true,
                addonEvents: addonEvents
                    .filter(ae => ae.eventId)
                    .map(ae => ({
                        eventId: ae.eventId,
                        label: (ae.label || "").trim(),
                        price: ae.price === "" ? 0 : (parseFloat(ae.price) || 0),
                    })),
                updatedAt: new Date().toISOString(),
                cardStyle: editFormData.cardStyle || { borderDash: false, borderColor: '#f97316', marqueeText: '', marqueeSpeed: 'normal' },
                multiEventDiscount: (editFormData.multiEventDiscount?.enabled && editFormData.isFreeEvent !== true)
                    ? {
                        enabled: true,
                        type: editFormData.multiEventDiscount.type || 'flat',
                        amount: parseFloat(editFormData.multiEventDiscount.amount) || 0,
                        scope: editFormData.multiEventDiscount.scope || 'sameVenue',
                        label: (editFormData.multiEventDiscount.label || '').trim(),
                    }
                    : { enabled: false },
                // Free event: price, services, early bird and multi-event
                // discount are all forced off/zero so nothing can charge.
                isFreeEvent: editFormData.isFreeEvent === true,
                freeLabel: (editFormData.freeLabel || '').trim().slice(0, 30),
                hideFreeLabel: editFormData.hideFreeLabel === true,
                earlyBird: (editFormData.earlyBird?.enabled && editFormData.isFreeEvent !== true)
                    ? {
                        enabled: true,
                        price: Math.round((parseFloat(editFormData.earlyBird.price) || 0) * 100) / 100,
                        cutoffHours: parseFloat(editFormData.earlyBird.cutoffHours) || 24,
                    }
                    : { enabled: false },
            };

            await updateDoc(doc(db, 'events', selectedEvent.id), updateData);
            
            setEvents(prevEvents => 
                prevEvents.map(event => 
                    event.id === selectedEvent.id 
                        ? { ...event, ...updateData }
                        : event
                )
            );

            if (showPastEvents) {
                setPastEvents(prevEvents => 
                    prevEvents.map(event => 
                        event.id === selectedEvent.id 
                            ? { ...event, ...updateData }
                            : event
                    )
                );
            }

            showNotification('Event updated successfully!', 'success');
            setShowEditModal(false);

            // ── Check if capacity increased and waitlist has people ──
            const oldMax = selectedEvent.maxParticipants || 0;
            const newMax = maxParticipants;
            const currentCount = selectedEvent.currentParticipants || 0;
            const waitlist = selectedEvent.waitlist || [];

            if (newMax > oldMax && currentCount < newMax && waitlist.length > 0) {
              const availableSpots = newMax - currentCount - (selectedEvent.reservedForPromotion || 0);
              const toPromote = Math.min(availableSpots, waitlist.length);
              const isPaid = !(editFormData.isFreeEvent === true || roundedPrice <= 0);

              setWaitlistPromoteModal({
                eventId: selectedEvent.id,
                eventTitle: selectedEvent.title || '',
                toPromote,
                waitlistCount: waitlist.length,
                waitlist: waitlist.slice(0, toPromote),
                oldMax,
                newMax,
                isPaid,
                promotionDeadlineHours: selectedEvent.promotionDeadlineHours || 1,
              });
            }
            
        } catch (error) {
            console.error('Error updating event:', error);
            showNotification(`Error updating event: ${error.message}`, 'error');
        } finally {
            setSavingEvent(false);
        }
    };

    // ── Waitlist promotion handler (triggered by custom modal) ──
    // WAITLIST FIX: no client-side promotion any more. Raising capacity
    // already makes the server trigger (notifyWaitlistOnSpotOpen) promote the
    // next players — paid events get a payment invitation, free events are
    // confirmed directly. This modal used to promote AGAIN on top of that,
    // double-counting spots on free events and double-emailing on paid ones.
    // It is now information only.
    const handlePromoteWaitlist = () => {
      setWaitlistPromoteModal(null);
    };

    const handleDuplicateEvent = async (event, e) => {
        if (e) e.stopPropagation();
        setDuplicatingEvent(event.id);
        try {
            const originalStart = new Date(event.startDate);
            const newStart = new Date(originalStart.getTime() + (7 * 24 * 60 * 60 * 1000));
            const originalEnd = new Date(event.endDate);
            const duration = (originalEnd - originalStart) / (1000 * 60 * 60);
            const newEnd = new Date(newStart.getTime() + (duration * 60 * 60 * 1000));
            
            const newRegDeadline = new Date(newStart.getTime() - (1 * 60 * 60 * 1000));

            // Find venue name
            const eventVenue = venues.find(v => v.id === event.venueId);
            const venueName = eventVenue?.name || event.venueName || event.location || 'Venue';

            // Format the new title
            const month = newStart.getMonth() + 1;
            const day = newStart.getDate();
            const startHour = newStart.getHours();
            const startMin = newStart.getMinutes();
            const endHour = newEnd.getHours();
            const endMin = newEnd.getMinutes();
            
            // Format time (5:30 -> 5.30, 17:00 -> 5.00pm)
            const formatTime = (hour, min) => {
                const isPM = hour >= 12;
                const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
                const displayMin = String(min).padStart(2, '0');
                return `${displayHour}.${displayMin}`;
            };

            const startTimeStr = formatTime(startHour, startMin);
            const endTimeStr = formatTime(endHour, endMin);
            
            // Determine if we need to show AM/PM
            const startIsPM = startHour >= 12;
            const endIsPM = endHour >= 12;
            const timeStr = startIsPM === endIsPM 
                ? `${startTimeStr}-${endTimeStr}${endIsPM ? 'pm' : 'am'}`
                : `${startTimeStr}${startIsPM ? 'pm' : 'am'}-${endTimeStr}${endIsPM ? 'pm' : 'am'}`;

            const durationHours = duration.toFixed(1);
            const newTitle = `${month}.${day} ${venueName} ${timeStr} (${durationHours}h)`;

            const duplicateData = {
                ...event,
                title: newTitle,
                startDate: newStart.toISOString(),
                endDate: newEnd.toISOString(),
                registrationDeadline: newRegDeadline.toISOString(),
                createdAt: new Date().toISOString(),
                createdBy: currentUser.uid,
                currentParticipants: 0,
                viewCount: 0,
                shareCount: 0,
                participants: [],
                waitlist: []
            };
            
            delete duplicateData.id;
            delete duplicateData.groupCounts;
            delete duplicateData._groupMoveNonce;
            delete duplicateData._groupMoveSpots;
            
            await addDoc(collection(db, 'events'), duplicateData);
            showNotification('Event duplicated successfully!', 'success');
        } catch (error) {
            console.error('Error duplicating event:', error);
            showNotification('Error duplicating event', 'error');
        } finally {
            setDuplicatingEvent(null);
        }
    };

    const handleMarkAsFull = async (event, e) => {
        e.stopPropagation();
        try {
            const eventRef = doc(db, 'events', event.id);
            const currentCount = event.currentParticipants || 0;
            
            if (event.markedAsFull) {
                // Unmark as full - restore original maxParticipants
                await updateDoc(eventRef, {
                    markedAsFull: false,
                    maxParticipants: event.originalMaxParticipants || event.maxParticipants
                });
                showNotification('Event unmarked as full', 'success');
            } else {
                // Mark as full - set maxParticipants to current count
                await updateDoc(eventRef, {
                    markedAsFull: true,
                    originalMaxParticipants: event.maxParticipants,
                    maxParticipants: currentCount
                });
                showNotification('Event marked as full', 'success');
            }
        } catch (error) {
            console.error('Error marking event as full:', error);
            showNotification('Failed to update event', 'error');
        }
    };

    const handleToggleHideEvent = async (event) => {
        try {
            const eventRef = doc(db, 'events', event.id);
            const newHidden = !event.hiddenFromList;
            await updateDoc(eventRef, { hiddenFromList: newHidden });
            setEvents(prev => prev.map(ev => ev.id === event.id ? { ...ev, hiddenFromList: newHidden } : ev));
            if (showPastEvents) {
                setPastEvents(prev => prev.map(ev => ev.id === event.id ? { ...ev, hiddenFromList: newHidden } : ev));
            }
            showNotification(newHidden ? 'Event hidden from list (still accessible via link)' : 'Event visible in list again', 'success');
        } catch (error) {
            console.error('Error toggling event visibility:', error);
            showNotification('Failed to update visibility', 'error');
        }
    };

    const handleDeleteEvent = (event, e) => {
        if (e) e.stopPropagation();
        
        // Check if event has participants
        if (event.currentParticipants && event.currentParticipants > 0) {
            showNotification(
                `Cannot delete event with ${event.currentParticipants} booking(s). Please cancel all bookings first.`,
                'error'
            );
            return;
        }
        
        setSelectedEvent(event);
        setShowDeleteModal(true);
    };

    const confirmDeleteEvent = async () => {
        if (!selectedEvent) return;

        setDeletingEvent(true);
        try {
            await deleteDoc(doc(db, "events", selectedEvent.id));
            showNotification("Event deleted successfully!", "success");
            setShowDeleteModal(false);
            if (showPastEvents) {
                fetchPastEvents();
            }
        } catch (error) {
            console.error("Error deleting event:", error);
            showNotification("Error deleting event", "error");
        } finally {
            setDeletingEvent(false);
        }
    };

    const navigatePeriod = (direction) => {
        const newDate = new Date(selectedPeriod);

        if (dateFilter === "month") {
            if (direction === "prev") {
                newDate.setMonth(newDate.getMonth() - 1);
            } else {
                newDate.setMonth(newDate.getMonth() + 1);
            }
        } else {
            if (direction === "prev") {
                newDate.setDate(newDate.getDate() - 7);
            } else {
                newDate.setDate(newDate.getDate() + 7);
            }
        }

        setSelectedPeriod(newDate);
    };

    const getPeriodString = () => {
        if (dateFilter === "month") {
            return selectedPeriod.toLocaleDateString("en-US", { month: "long", year: "numeric" });
        } else {
            const startOfWeek = new Date(selectedPeriod);
            const dayOfWeek = startOfWeek.getDay();
            startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);

            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);

            return `${startOfWeek.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${endOfWeek.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
        }
    };

    const getPastEventsSummary = () => {
        if (pastEvents.length === 0) return null;

        const totalEvents = pastEvents.length;
        const totalParticipants = pastEvents.reduce((sum, event) => sum + (event.currentParticipants || 0), 0);
        const totalRevenue = pastEvents.reduce((sum, event) => {
            const participants = event.currentParticipants || 0;
            const price = event.price || 0;
            return sum + (participants * price);
        }, 0);
        const totalViews = pastEvents.reduce((sum, event) => sum + (event.viewCount || 0), 0);
        const avgParticipants = totalEvents > 0 ? (totalParticipants / totalEvents).toFixed(1) : 0;
        const totalCapacity = pastEvents.reduce((sum, event) => sum + (event.maxParticipants || 0), 0);
        const fillRate = totalCapacity > 0 ? ((totalParticipants / totalCapacity) * 100).toFixed(0) : 0;
        const fullEvents = pastEvents.filter(e => (e.currentParticipants || 0) >= (e.maxParticipants || 1)).length;
        const avgPrice = totalEvents > 0 ? (pastEvents.reduce((s, e) => s + (e.price || 0), 0) / totalEvents).toFixed(2) : 0;
        const totalWaitlisted = pastEvents.reduce((sum, e) => sum + (e.waitlist?.length || 0), 0);

        return {
            totalEvents,
            totalParticipants,
            totalRevenue: totalRevenue.toFixed(2),
            totalViews,
            avgParticipants,
            fillRate,
            fullEvents,
            avgPrice,
            totalWaitlisted,
            totalCapacity,
        };
    };

    const filteredEvents = getFilteredEvents();
    const groupedEvents = groupEventsByDate(filteredEvents);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Custom card style animations */}
            <style>{`
                @keyframes marquee-scroll { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
                .marquee-slow   { animation: marquee-scroll 20s linear infinite; }
                .marquee-normal { animation: marquee-scroll 10s linear infinite; }
                .marquee-fast   { animation: marquee-scroll 5s  linear infinite; }
                @keyframes border-march {
                    to { background-position: 20px 0, 100% 20px, -20px 100%, 0 -20px; }
                }
                .dashed-border-animated {
                    background-image:
                        repeating-linear-gradient(90deg,  var(--cbd) 0, var(--cbd) 8px, transparent 8px, transparent 16px),
                        repeating-linear-gradient(180deg, var(--cbd) 0, var(--cbd) 8px, transparent 8px, transparent 16px),
                        repeating-linear-gradient(90deg,  var(--cbd) 0, var(--cbd) 8px, transparent 8px, transparent 16px),
                        repeating-linear-gradient(180deg, var(--cbd) 0, var(--cbd) 8px, transparent 8px, transparent 16px);
                    background-size: 20px 2px, 2px 20px, 20px 2px, 2px 20px;
                    background-position: 0 0, 100% 0, 0 100%, 0 0;
                    background-repeat: repeat-x, repeat-y, repeat-x, repeat-y;
                    animation: border-march 0.5s linear infinite;
                    border: none !important;
                    will-change: background-position;
                }
                @media (max-width: 640px) {
                    .dashed-border-animated {
                        animation-duration: 1s;
                    }
                }
            `}</style>
            {/* Fixed Header */}
            <div ref={fixedHeaderRef} className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
                {/* View toggle: list feed vs calendar grid */}
                <div className="px-3 pt-2 pb-1 flex justify-end">
                    <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
                        <button
                            onClick={() => setDisplayMode("list")}
                            className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                                displayMode === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                            }`}
                        >{t('home.listView')}</button>
                        <button
                            onClick={() => setDisplayMode("calendar")}
                            className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                                displayMode === "calendar" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                            }`}
                        >{t('home.calendarView')}</button>
                    </div>
                </div>

                {/* Scrollable Date Selector - Only dates with events. Hidden in
                    calendar mode since the month grid replaces it. */}
                {displayMode === "list" && (
                <div className="overflow-x-auto scrollbar-hide px-3 pt-1 pb-2">
                    <div className="flex gap-2 min-w-max">
                        {(() => {
                            const eventDates = [...new Set(events.map(event => {
                                const date = new Date(event.startDate);
                                date.setHours(0, 0, 0, 0);
                                return date.getTime();
                            }))].sort((a, b) => a - b).map(timestamp => new Date(timestamp));

                            return eventDates.map((date, index) => {
                                const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
                                const dayNumber = date.getDate();
                                const monthName = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
                                const isTodayDate = isToday(date);
                                const isSelectedDate = isSelected(date);

                                return (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            setSelectedDate(date);
                                            setViewMode("day");
                                        }}
                                        className={`flex flex-col items-center justify-center px-2.5 py-2 rounded-lg transition-all min-w-[50px] ${
                                            isSelectedDate && viewMode === "day"
                                                ? "bg-orange-500 text-white shadow-md"
                                                : isTodayDate
                                                ? "bg-white text-gray-700 border-2 border-orange-500"
                                                : "bg-white text-gray-700 border border-gray-200"
                                        }`}
                                    >
                                        <span className="text-[10px] font-medium">{dayName}</span>
                                        <span className="text-lg font-bold my-0.5">{dayNumber}</span>
                                        <span className="text-[10px]">{monthName}</span>
                                        {isTodayDate && (
                                            <span className={`text-[10px] mt-0.5 font-medium ${isSelectedDate && viewMode === "day" ? 'text-white' : 'text-orange-500'}`}>{t('common.today')}</span>
                                        )}
                                    </button>
                                );
                            });
                        })()}
                    </div>
                </div>
                )}

                {/* Filters Section */}
                <div className="px-3 pb-2">
                    <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
                        <button
                            onClick={() => {
                                setViewMode("all");
                                setSearchTerm("");
                            }}
                            className={`px-3 py-1.5 rounded-lg font-medium whitespace-nowrap transition-all text-xs ${
                                viewMode === "all"
                                    ? "bg-orange-500 text-white"
                                    : "bg-white text-gray-600 border border-gray-200"
                            }`}
                        >{t('home.allEvents')}</button>
                        <button
                            onClick={() => setSearchTerm("")}
                            className={`px-3 py-1.5 rounded-lg font-medium whitespace-nowrap transition-all text-xs ${
                                searchTerm === ""
                                    ? "bg-orange-500 text-white"
                                    : "bg-white text-gray-600 border border-gray-200"
                            }`}
                        >{t('home.allLocations')}</button>
                        
                        {/* Show unique venue names from events */}
                        {(() => {
                            const uniqueVenues = [...new Set(
                                events
                                    .map(event => {
                                        const venue = venues.find(v => v.id === event.venueId);
                                        return venue?.name || event.venueName || null;
                                    })
                                    .filter(venueName => venueName !== null)
                            )];

                            return uniqueVenues.map((venueName, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSearchTerm(venueName)}
                                    className={`px-3 py-1.5 rounded-lg font-medium whitespace-nowrap transition-all text-xs ${
                                        searchTerm === venueName
                                            ? "bg-blue-500 text-white"
                                            : "bg-white text-gray-600 border border-gray-200"
                                    }`}
                                >
                                    {venueName}
                                </button>
                            ));
                        })()}
                    </div>
                </div>

            </div>

            {/* Main Content - with top padding to account for fixed header */}
            <div className="px-3 pb-24" style={{ paddingTop: `${(fixedHeaderHeight || 150) + 10}px` }}>
                {displayMode === "calendar" ? (
                    <CalendarMonthView
                        month={calendarMonth}
                        onMonthChange={(m) => { setCalendarMonth(m); setCalendarSelectedDay(null); }}
                        eventsByDay={buildEventsByDayKey(getCalendarEvents())}
                        selectedDay={calendarSelectedDay}
                        onSelectDay={(date) => setCalendarSelectedDay(date)}
                        onEventClick={handleEventClick}
                        venues={venues}
                        avatarMap={avatarMap}
                        currentUser={currentUser}
                        isAdmin={isAdmin}
                        getEventRegistration={getEventRegistration}
                        getEventStatus={getEventStatus}
                        handleMarkAsFull={handleMarkAsFull}
                        handleEditEvent={handleEditEvent}
                        handleDuplicateEvent={handleDuplicateEvent}
                        handleDeleteEvent={handleDeleteEvent}
                        handleShare={handleShare}
                        handleToggleHideEvent={handleToggleHideEvent}
                        duplicatingEvent={duplicatingEvent}
                        danglingPaymentEventIds={danglingPaymentEventIds}
                        activeChallengesMap={activeChallengesMap}
                        t={t}
                        lang={lang}
                        pendingPromotionsMap={pendingPromotionsMap}
                    />
                ) : loading ? (
                    // Skeleton cards instead of a blank gap — mimics a "today" group
                    // with a few placeholder cards, each fading in with a slight stagger.
                    <div className="mb-3">
                        <div className="h-3.5 skeleton-shimmer rounded w-24 mb-2.5 ml-2" />
                        <div className="space-y-5">
                            {[0, 1, 2].map((i) => (
                                <EventCardSkeleton key={i} delay={i * 90} showAdminRow={isAdmin} />
                            ))}
                        </div>
                    </div>
                ) : filteredEvents.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow p-8 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('home.noEventsFound')}</h3>
                        <p className="text-gray-600 text-sm">
                            {viewMode === "day" 
                                ? "No events scheduled for this day."
                                : "No upcoming events found."}
                        </p>
                    </div>
                ) : (
                    Object.entries(groupedEvents).map(([dateKey, dateEvents], groupIndex) => (
                        <div key={dateKey} className="mb-3">
                            {/* Date Header */}
                            <div className="text-orange-500 font-bold text-sm mb-2.5 px-2 uppercase tracking-wide">
                                {formatDateHeader(dateEvents[0]?.startDate)}
                            </div>

                            {/* Events List — staggered fade-in only on the very first render
                                after data arrives (justLoadedRef), so later re-renders from
                                live Firestore updates don't replay the animation. Each card is
                                also wrapped in GravityCard, which gives it a "settling into
                                place" feel the first time it scrolls into view — independent
                                of the initial-load stagger, since that's a one-time data event
                                while this is an ongoing scroll interaction. */}
                            <div className="space-y-5">
                                {dateEvents.map((event, cardIndex) => {
                                    const overallIndex = groupIndex * 100 + cardIndex; // stagger order across groups
                                    const cardStyle = justLoadedRef.current
                                        ? { opacity: 0, animation: `cardFadeIn 0.35s ease-out ${Math.min(overallIndex * 60, 600)}ms forwards` }
                                        : undefined;
                                    return (
                                        <div key={event.id} style={cardStyle}>
                                            <GravityCard>
                                                <EventCard
                                                    event={event}
                                                    venues={venues}
                                                    avatarMap={avatarMap}
                                                    currentUser={currentUser}
                                                    isAdmin={isAdmin}
                                                    t={t}
                                                    getEventRegistration={getEventRegistration}
                                                    getEventStatus={getEventStatus}
                                                    onCardClick={handleEventClick}
                                                    handleMarkAsFull={handleMarkAsFull}
                                                    handleEditEvent={handleEditEvent}
                                                    handleDuplicateEvent={handleDuplicateEvent}
                                                    handleDeleteEvent={handleDeleteEvent}
                                                    handleShare={handleShare}
                                                    handleToggleHideEvent={handleToggleHideEvent}
                                                    duplicatingEvent={duplicatingEvent}
                                                    hasDanglingPayment={danglingPaymentEventIds.has(event.id)}
                                                    activeChallenges={activeChallengesMap[event.id] || []}
                                                    lang={lang}
                                                    pendingPromotion={pendingPromotionsMap[event.id] || null}
                                                />
                                            </GravityCard>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Floating Admin Button - Settings Icon with Expandable Menu */}
            {isAdmin && (
                <div className="fixed bottom-20 right-6 flex flex-col gap-3 z-40 items-end">
                    {showAdminMenu && (
                        <>
                             <button
                                onClick={handleShareAllUpcoming}
                                className="bg-green-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-green-700 transition-all flex items-center gap-2 text-sm font-medium"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>{t('home.shareAllUpcoming')}</button>
                            <button
                                onClick={() => {
                                    navigate('/create-event');
                                    setShowAdminMenu(false);
                                }}
                                className="bg-green-500 text-white px-4 py-3 rounded-full shadow-lg hover:bg-green-600 transition-all flex items-center gap-2 text-sm font-medium"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>{t('home.createEvents')}</button>
                           
                            <button
                                onClick={() => {
                                    setShowPastEvents(!showPastEvents);
                                    setShowAdminMenu(false);
                                }}
                                className="bg-gray-700 text-white px-4 py-3 rounded-full shadow-lg hover:bg-gray-800 transition-all text-sm font-medium flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {showPastEvents ? "Hide Past" : "Past Events"}
                            </button>
                        </>
                    )}
                    <button
                        onClick={() => setShowAdminMenu(!showAdminMenu)}
                        className="bg-orange-500 text-white p-3 rounded-full shadow-lg hover:bg-orange-600 transition-all hover:scale-110"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Past Events Section - Mobile Modal */}
            {isAdmin && showPastEvents && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center">
                    <div className="bg-gray-50 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg overflow-hidden flex flex-col" style={{ maxHeight: '92dvh' }}>
                        {/* Header */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 pt-4 pb-3 z-10">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="text-lg font-bold text-gray-900">{t('home.pastEvents')}</h2>
                                <button
                                    onClick={() => setShowPastEvents(false)}
                                    className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            
                            {/* Period selector */}
                            <div className="flex items-center gap-2">
                                <select
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none"
                                >
                                    <option value="month">{t('home.byMonth')}</option>
                                    <option value="week">{t('home.byWeek')}</option>
                                </select>
                                <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200">
                                    <button onClick={() => navigatePeriod("prev")} className="p-2 hover:bg-gray-100 rounded-l-lg transition-colors">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <span className="px-3 text-sm font-medium text-gray-700 whitespace-nowrap">{getPeriodString()}</span>
                                    <button onClick={() => navigatePeriod("next")} className="p-2 hover:bg-gray-100 rounded-r-lg transition-colors">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto">
                            {/* Summary Statistics */}
                            {!loadingPastEvents && pastEvents.length > 0 && (() => {
                                const summary = getPastEventsSummary();
                                return (
                                    <div className="px-4 pt-3 pb-1">
                                        {/* Primary stats row */}
                                        <div className="grid grid-cols-3 gap-2 mb-2">
                                            <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
                                                <div className="text-2xl font-bold text-gray-900">{summary.totalEvents}</div>
                                                <div className="text-[10px] text-gray-500 font-medium mt-0.5">{t('home.totalEvents')}</div>
                                            </div>
                                            <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
                                                <div className="text-2xl font-bold text-sky-600">{summary.totalParticipants}</div>
                                                <div className="text-[10px] text-gray-500 font-medium mt-0.5">{t('home.totalBookings')}</div>
                                            </div>
                                            <div className="bg-white rounded-xl p-3 text-center border border-gray-100">
                                                <div className="text-2xl font-bold text-green-600">${summary.totalRevenue}</div>
                                                <div className="text-[10px] text-gray-500 font-medium mt-0.5">{t('home.totalRevenue')}</div>
                                            </div>
                                        </div>
                                        {/* Secondary stats row */}
                                        <div className="grid grid-cols-4 gap-1.5 mb-3">
                                            <div className="bg-white rounded-lg px-2 py-2 text-center border border-gray-100">
                                                <div className="text-sm font-bold text-purple-600">{summary.avgParticipants}</div>
                                                <div className="text-[9px] text-gray-400 font-medium">{t('home.avgBookings')}</div>
                                            </div>
                                            <div className="bg-white rounded-lg px-2 py-2 text-center border border-gray-100">
                                                <div className="text-sm font-bold text-orange-600">{summary.fillRate}%</div>
                                                <div className="text-[9px] text-gray-400 font-medium">Fill Rate</div>
                                            </div>
                                            <div className="bg-white rounded-lg px-2 py-2 text-center border border-gray-100">
                                                <div className="text-sm font-bold text-red-500">{summary.fullEvents}/{summary.totalEvents}</div>
                                                <div className="text-[9px] text-gray-400 font-medium">Sold Out</div>
                                            </div>
                                            <div className="bg-white rounded-lg px-2 py-2 text-center border border-gray-100">
                                                <div className="text-sm font-bold text-gray-600">{summary.totalViews}</div>
                                                <div className="text-[9px] text-gray-400 font-medium">{t('home.totalViews')}</div>
                                            </div>
                                        </div>
                                        {/* Additional insights bar */}
                                        <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100 text-[10px] text-gray-500 mb-1">
                                            <span>Unique Players: <span className="font-semibold text-sky-700">{pastEventRegStats.uniqueUsers}</span></span>
                                            <span className="text-gray-300">|</span>
                                            <span>Guests: <span className="font-semibold text-indigo-600">{pastEventRegStats.totalGuests}</span></span>
                                            <span className="text-gray-300">|</span>
                                            <span>Waitlisted: <span className="font-semibold text-amber-600">{summary.totalWaitlisted}</span></span>
                                        </div>
                                        <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100 text-[10px] text-gray-500 mb-2">
                                            <span>Avg Price: <span className="font-semibold text-gray-700">${summary.avgPrice}</span></span>
                                            <span className="text-gray-300">|</span>
                                            <span>Capacity: <span className="font-semibold text-gray-700">{summary.totalCapacity}</span></span>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Event List */}
                            <div className="px-4 pb-6">
                                {loadingPastEvents ? (
                                    <div className="flex justify-center py-16">
                                        <div className="w-10 h-10 border-3 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                ) : pastEvents.length === 0 ? (
                                    <div className="text-center py-16">
                                        <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                            <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <p className="text-gray-500 text-sm">{t('home.noPastEvents')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 mb-18">
                                        {pastEvents.map((event) => {
                                            const eventVenue = venues.find(v => v.id === event.venueId);
                                            // Same helper as the public cards — the admin list used to
                                            // ignore reservedForPromotion entirely, so the same event
                                            // showed a different fill level here than on the home feed.
                                            const counts = getEventCounts(event);
                                            const isFull = counts.isFull;
                                            const fillPct = counts.max ? Math.min(100, Math.round((counts.occupied / counts.max) * 100)) : 0;
                                            return (
                                                <div key={event.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                                                    <div className="p-3">
                                                        {/* Title + venue row */}
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <div className="min-w-0 flex-1">
                                                                <h3 className="font-semibold text-gray-900 text-sm truncate">{event.title}</h3>
                                                                <p className="text-xs text-gray-500 mt-0.5">
                                                                    {new Date(event.startDate).toLocaleDateString('en-AU', {
                                                                        weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit'
                                                                    })}
                                                                    {eventVenue ? ` · ${eventVenue.name}` : ''}
                                                                </p>
                                                            </div>
                                                            {isFull && (
                                                                <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-red-100 text-red-600 rounded flex-shrink-0">FULL</span>
                                                            )}
                                                        </div>

                                                        {/* Stats bar */}
                                                        <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-2">
                                                            <span className="flex items-center gap-1">
                                                                <svg className="w-3 h-3 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                </svg>
                                                                <span className="font-medium text-gray-700">{counts.occupied}</span>/{counts.max}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                </svg>
                                                                {event.viewCount || 0}
                                                            </span>
                                                            {event.price > 0 && (
                                                                <span className="flex items-center gap-1 text-green-600 font-medium">
                                                                    ${((event.currentParticipants || 0) * event.price).toFixed(0)}
                                                                </span>
                                                            )}
                                                            {/* counts.waiting, not waitlist.length: a promoted-but-
                                                                unpaid player sits in the waitlist array AND in
                                                                reservedForPromotion, so the raw length counted them
                                                                as both joined and waitlisted at the same time. */}
                                                            {counts.waiting > 0 && (
                                                                <span className="flex items-center gap-1 text-amber-600">
                                                                    +{counts.waiting} waitlist
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Fill bar */}
                                                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2.5">
                                                            <div
                                                                className={`h-full rounded-full transition-all ${fillPct >= 100 ? 'bg-red-400' : fillPct >= 75 ? 'bg-orange-400' : 'bg-sky-400'}`}
                                                                style={{ width: `${fillPct}%` }}
                                                            />
                                                        </div>

                                                        {/* Registration timeline — per-hour bar chart */}
                                                        {pastEventRegTimes[event.id] && pastEventRegTimes[event.id].length > 2 && (() => {
                                                            const regTimes = pastEventRegTimes[event.id];
                                                            const eventStart = new Date(event.startDate);
                                                            const regDates = regTimes.map(rt => new Date(rt)).sort((a, b) => a - b);
                                                            // Start from 2nd registration to skip early outliers
                                                            const startFrom = regDates.length > 2 ? regDates[1] : regDates[0];
                                                            const spanHours = Math.max(1, Math.ceil((eventStart - startFrom) / (1000 * 60 * 60)));
                                                            const totalBuckets = Math.min(spanHours, 168);
                                                            if (totalBuckets < 1) return null;

                                                            const buckets = new Array(totalBuckets).fill(0);
                                                            regDates.forEach(rd => {
                                                                const hBefore = Math.floor((eventStart - rd) / (1000 * 60 * 60));
                                                                const idx = totalBuckets - 1 - Math.min(Math.max(0, hBefore), totalBuckets - 1);
                                                                if (idx >= 0 && idx < totalBuckets) buckets[idx]++;
                                                            });

                                                            // Trim leading empty buckets
                                                            let firstNonEmpty = 0;
                                                            while (firstNonEmpty < buckets.length && buckets[firstNonEmpty] === 0) firstNonEmpty++;
                                                            if (firstNonEmpty > 0) firstNonEmpty = Math.max(0, firstNonEmpty - 1); // keep 1 empty before first bar
                                                            const trimmedBuckets = buckets.slice(firstNonEmpty);
                                                            const trimOffset = firstNonEmpty;

                                                            let peakIdx = 0, peakCount = 0;
                                                            const data = trimmedBuckets.map((count, i) => {
                                                                if (count > peakCount) { peakCount = count; peakIdx = i; }
                                                                const hBack = totalBuckets - 1 - (i + trimOffset);
                                                                const slotEnd = new Date(eventStart.getTime() - hBack * 3600000);
                                                                const timeLabel = slotEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                                                                return { label: timeLabel, count, hBack };
                                                            });

                                                            const peakData = data[peakIdx];
                                                            const peakText = peakCount > 1 ? `🔥 ${peakCount} regs around ${peakData?.label || ''} (-${peakData?.hBack || 0}h)` : null;
                                                            const barWidth = 18;
                                                            const chartWidth = Math.max(data.length * barWidth, 280);
                                                            const needsScroll = chartWidth > 300;
                                                            // Show every Nth label to avoid crowding
                                                            const tickEvery = data.length > 30 ? 6 : data.length > 16 ? 4 : data.length > 8 ? 2 : 1;

                                                            return (
                                                            <div className="mb-2.5 bg-gray-50 rounded-lg p-2 border border-gray-100">
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-[10px] font-semibold text-gray-500">Registrations / hour</span>
                                                                    <span className="text-[10px] text-gray-400">{regTimes.length} total</span>
                                                                </div>
                                                                <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                                                                    <div style={{ width: needsScroll ? chartWidth : '100%', height: 80 }}>
                                                                        <Suspense fallback={<div style={{ height: 80 }} />}>
                                                                            <RegistrationsBarChart data={data} tickEvery={tickEvery} />
                                                                        </Suspense>
                                                                    </div>
                                                                </div>
                                                                {needsScroll && (
                                                                    <div className="text-[8px] text-gray-300 text-center mt-0.5">← scroll →</div>
                                                                )}
                                                                {peakText && (
                                                                    <div className="text-[10px] text-orange-600 font-medium mt-1">{peakText}</div>
                                                                )}
                                                            </div>
                                                            );
                                                        })()}

                                                        {/* Action buttons */}
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => { setShowPastEvents(false); handleEventClick(event.id); }}
                                                                className="flex-1 px-3 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                                                            >{t('common.view')}</button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleDuplicateEvent(event, e); }}
                                                                className="px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                                                                disabled={duplicatingEvent === event.id}
                                                            >{duplicatingEvent === event.id ? "..." : "Duplicate"}</button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setShowPastEvents(false); handleDeleteEvent(event, e); }}
                                                                className="p-1.5 text-xs font-medium border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                                                            >
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal - Mobile Friendly */}
            {showEditModal && isAdmin && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[9999] p-0 sm:p-4">
                    <div className="bg-white rounded-t-3xl sm:rounded-xl w-full sm:max-w-2xl overflow-hidden flex flex-col" style={{ maxHeight: '90dvh' }}>
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between">
                            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{t('home.editEvent')}</h3>
                            <button
                                onClick={() => setShowEditModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.title')}</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                                    value={editFormData.title || ''}
                                    onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.description')}</label>
                                <textarea
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                                    rows="3"
                                    value={editFormData.description || ''}
                                    onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.organizer')}</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                                    value={editFormData.organizer || ''}
                                    onChange={(e) => setEditFormData({...editFormData, organizer: e.target.value})}
                                    placeholder="e.g., Wangzai Panda Badminton Club"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.startDate')}</label>
                                    <input
                                        type="datetime-local"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
                                        value={editFormData.startDate || ''}
                                        onChange={(e) => setEditFormData({...editFormData, startDate: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.endDate')}</label>
                                    <input
                                        type="datetime-local"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
                                        value={editFormData.endDate || ''}
                                        onChange={(e) => setEditFormData({...editFormData, endDate: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.venue')}</label>
                                <select
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                                    value={editFormData.venueId || ''}
                                    onChange={handleVenueChange}
                                >
                                    <option value="">{t('home.selectVenue')}</option>
                                    {venues.map(venue => (
                                        <option key={venue.id} value={venue.id}>
                                            {venue.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {availableCourts.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.courts')}</label>
                                    <div className="flex flex-wrap gap-2">
                                        {availableCourts.map((court) => (
                                            <button
                                                key={court}
                                                type="button"
                                                onClick={() => handleCourtSelection(court)}
                                                className={`px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                                                    editFormData.selectedCourts?.includes(court)
                                                        ? "bg-orange-500 text-white"
                                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                }`}
                                            >
                                                {court}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.price')}</label>
                                    <input
                                        type="number"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                                        value={editFormData.price || 0}
                                        onChange={(e) => setEditFormData({...editFormData, price: e.target.value})}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('home.maxParticipants')}</label>
                                    <div className="flex items-center gap-0">
                                        <button
                                            type="button"
                                            onClick={() => setEditFormData({...editFormData, maxParticipants: Math.max(1, (parseInt(editFormData.maxParticipants, 10) || 0) - 1)})}
                                            className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-l-lg text-gray-600 font-semibold text-sm transition-colors"
                                        >−</button>
                                        <input
                                            type="number"
                                            className="w-14 min-w-[3.5rem] px-1 py-1.5 border-t border-b border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-center text-sm font-semibold"
                                            value={editFormData.maxParticipants || 0}
                                            onChange={(e) => setEditFormData({...editFormData, maxParticipants: e.target.value})}
                                            min="1"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setEditFormData({...editFormData, maxParticipants: (parseInt(editFormData.maxParticipants, 10) || 0) + 1})}
                                            className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-r-lg text-gray-600 font-semibold text-sm transition-colors"
                                        >+</button>
                                    </div>
                                    {(editFormData.groups || []).length > 0 && (
                                        <p className="mt-1 text-xs text-violet-700">= 分组人数合计 {totalGroupCapacity(editFormData.groups)}</p>
                                    )}
                                </div>
                            </div>

                            {/* Groups (Beginner / Advanced …) */}
                            <EventGroupsEditor
                                groups={editFormData.groups || []}
                                onChange={(groups) => setEditFormData((prev) => ({ ...prev, groups }))}
                                courts={(editFormData.selectedCourts || []).length ? editFormData.selectedCourts : availableCourts}
                                basePrice={editFormData.isFreeEvent ? 0 : editFormData.price}
                                isFreeEvent={editFormData.isFreeEvent === true}
                                maxParticipants={editFormData.maxParticipants}
                                counts={selectedEvent?.groupCounts || null}
                            />

                            {/* Multi-Event Discount */}
                            <div className="border border-emerald-200 rounded-xl p-3 bg-emerald-50/50">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                        🏷️ Multi-Event Discount 连报优惠
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setEditFormData({
                                            ...editFormData,
                                            multiEventDiscount: {
                                                ...(editFormData.multiEventDiscount || {}),
                                                enabled: !(editFormData.multiEventDiscount?.enabled),
                                            }
                                        })}
                                        className={`relative w-10 h-5 rounded-full transition-colors ${editFormData.multiEventDiscount?.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                    >
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${editFormData.multiEventDiscount?.enabled ? 'translate-x-5' : ''}`} />
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mb-2">Players who already registered for another event get a discount on this one. 已报名其他活动的球友可享折扣。</p>

                                {editFormData.multiEventDiscount?.enabled && (
                                    <div className="space-y-2 pt-1">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-xs text-gray-600 mb-0.5 block">Type 类型</label>
                                                <select
                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none bg-white"
                                                    value={editFormData.multiEventDiscount?.type || 'flat'}
                                                    onChange={(e) => setEditFormData({
                                                        ...editFormData,
                                                        multiEventDiscount: { ...editFormData.multiEventDiscount, type: e.target.value }
                                                    })}
                                                >
                                                    <option value="flat"> 固定减免</option>
                                                    <option value="percentage">百分比折扣</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-600 mb-0.5 block">
                                                    Amount {editFormData.multiEventDiscount?.type === 'percentage' ? '(%)' : '($)'}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                                                    value={editFormData.multiEventDiscount?.amount ?? ''}
                                                    onChange={(e) => setEditFormData({
                                                        ...editFormData,
                                                        multiEventDiscount: { ...editFormData.multiEventDiscount, amount: e.target.value }
                                                    })}
                                                    min="0"
                                                    step={editFormData.multiEventDiscount?.type === 'percentage' ? '1' : '0.01'}
                                                    placeholder={editFormData.multiEventDiscount?.type === 'percentage' ? 'e.g. 10' : 'e.g. 2'}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-600 mb-0.5 block">Scope 范围</label>
                                            <select
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none bg-white"
                                                value={editFormData.multiEventDiscount?.scope || 'sameDayVenue'}
                                                onChange={(e) => setEditFormData({
                                                    ...editFormData,
                                                    multiEventDiscount: { ...editFormData.multiEventDiscount, scope: e.target.value }
                                                })}
                                            >
                                                <option value="sameDayVenue">* 同日同场馆</option>
                                                <option value="sameVenue">同场馆任意日</option>
                                                <option value="any">任意活动</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-600 mb-0.5 block">Custom label (optional) 自定义标签</label>
                                            <input
                                                type="text"
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                                                value={editFormData.multiEventDiscount?.label || ''}
                                                onChange={(e) => setEditFormData({
                                                    ...editFormData,
                                                    multiEventDiscount: { ...editFormData.multiEventDiscount, label: e.target.value }
                                                })}
                                                placeholder="e.g. Same-day combo 当日连报优惠"
                                            />
                                        </div>
                                        {editFormData.multiEventDiscount?.amount && (
                                            <div className="text-xs text-emerald-700 bg-emerald-100 rounded-lg px-2.5 py-1.5">
                                                Preview: Players save {editFormData.multiEventDiscount.type === 'percentage'
                                                    ? `${editFormData.multiEventDiscount.amount}%`
                                                    : `$${parseFloat(editFormData.multiEventDiscount.amount || 0).toFixed(2)}`
                                                } when they've registered for {
                                                    editFormData.multiEventDiscount.scope === 'sameDayVenue' ? 'another event at the same venue on the same day'
                                                    : editFormData.multiEventDiscount.scope === 'sameVenue' ? 'another event at the same venue (any day)'
                                                    : 'any other event'
                                                }.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Free event 免费活动 */}
                            <div className="border border-emerald-200 rounded-xl p-3 bg-emerald-50/60">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                        Free event 免费活动
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setEditFormData({ ...editFormData, isFreeEvent: !editFormData.isFreeEvent })}
                                        className={`relative w-10 h-5 rounded-full transition-colors ${editFormData.isFreeEvent ? 'bg-emerald-500' : 'bg-gray-300'}`}
                                    >
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${editFormData.isFreeEvent ? 'translate-x-5' : ''}`} />
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500">No payment — players register instantly and can cancel / re-register any time; waitlist auto-confirms. Price, service prices, early bird and multi-event discount are ignored. 无需付款，报名即确认，可随时取消；候补自动转正。价格、服务费、早鸟、连报优惠不生效。</p>
                                {editFormData.isFreeEvent && (
                                    <div className="mt-2 space-y-1.5">
                                        <label className="text-xs text-gray-600 block">Card label 卡片显示文字</label>
                                        <input
                                            type="text"
                                            maxLength={30}
                                            value={editFormData.freeLabel || ''}
                                            disabled={editFormData.hideFreeLabel}
                                            onChange={(e) => setEditFormData({ ...editFormData, freeLabel: e.target.value })}
                                            placeholder="FREE 免费 (e.g. 占位活动 / AA制 / Members only)"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-400"
                                        />
                                        <label className="flex items-center gap-2 text-xs text-gray-600">
                                            <input
                                                type="checkbox"
                                                checked={!!editFormData.hideFreeLabel}
                                                onChange={(e) => setEditFormData({ ...editFormData, hideFreeLabel: e.target.checked })}
                                            />
                                            Don't show a label on the card 卡片不显示标签
                                        </label>
                                    </div>
                                )}
                                {editFormData.isFreeEvent && (selectedEvent?.price > 0) && (
                                    <p className="text-xs text-amber-700 mt-1.5">⚠️ Players who already paid for this event are NOT refunded automatically. 已付款的玩家不会自动退款。</p>
                                )}
                            </div>

                            {/* Early Bird Pricing 早鸟价 */}
                            <div className="border border-amber-200 rounded-xl p-3 bg-amber-50/50">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                        🐦 Early Bird 早鸟价
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setEditFormData({
                                            ...editFormData,
                                            earlyBird: {
                                                ...(editFormData.earlyBird || {}),
                                                enabled: !(editFormData.earlyBird?.enabled),
                                            }
                                        })}
                                        className={`relative w-10 h-5 rounded-full transition-colors ${editFormData.earlyBird?.enabled ? 'bg-amber-500' : 'bg-gray-300'}`}
                                    >
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${editFormData.earlyBird?.enabled ? 'translate-x-5' : ''}`} />
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mb-2">Offer a lower price for early registrations. Reverts to normal price before the event starts. 早期报名可享优惠价，临近活动自动恢复原价。</p>

                                {editFormData.earlyBird?.enabled && (
                                    <div className="space-y-2 pt-1">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-xs text-gray-600 mb-0.5 block">Early Bird Price 早鸟价 ($)</label>
                                                <input
                                                    type="number"
                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                                                    value={editFormData.earlyBird?.price ?? ''}
                                                    onChange={(e) => setEditFormData({
                                                        ...editFormData,
                                                        earlyBird: { ...editFormData.earlyBird, price: e.target.value }
                                                    })}
                                                    min="0"
                                                    step="0.01"
                                                    placeholder="e.g. 8"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-600 mb-0.5 block">Deadline 截止时间</label>
                                                <select
                                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none bg-white"
                                                    value={editFormData.earlyBird?.cutoffHours ?? 24}
                                                    onChange={(e) => setEditFormData({
                                                        ...editFormData,
                                                        earlyBird: { ...editFormData.earlyBird, cutoffHours: parseFloat(e.target.value) }
                                                    })}
                                                >
                                                    <option value={0.0167}>1 min before 提前1分钟</option>
                                                    <option value={0.083}>5 mins before 提前5分钟</option>
                                                    <option value={0.5}>30 mins before 提前30分钟</option>
                                                    <option value={1}>1 hour before 提前1小时</option>
                                                    <option value={6}>6 hours before 提前6小时</option>
                                                    <option value={12}>12 hours before 提前12小时</option>
                                                    <option value={24}>1 day before 提前1天</option>
                                                    <option value={48}>2 days before 提前2天</option>
                                                    <option value={72}>3 days before 提前3天</option>
                                                    <option value={168}>1 week before 提前1周</option>
                                                </select>
                                            </div>
                                        </div>
                                        {editFormData.earlyBird?.price && editFormData.price && (
                                            <div className="text-xs text-amber-700 bg-amber-100 rounded-lg px-2.5 py-1.5">
                                                Preview: Early bird ${parseFloat(editFormData.earlyBird.price || 0).toFixed(2)} → Normal ${parseFloat(editFormData.price || 0).toFixed(2)}.
                                                {' '}Switches {(() => {
                                                    const h = editFormData.earlyBird.cutoffHours || 24;
                                                    if (h < 1) return `${Math.round(h * 60)}m`;
                                                    return `${h}h`;
                                                })()} before event.
                                                {' '}Save ${(parseFloat(editFormData.price || 0) - parseFloat(editFormData.earlyBird.price || 0)).toFixed(2)}.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Add-on Events — let players optionally register for other events in one payment */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('home.addonEvents')}
                                    <span className="text-xs text-gray-400 font-normal ml-1">({t('home.addonEventsHint')})</span>
                                </label>

                                {addonEvents.length > 0 && (
                                    <div className="space-y-2 mb-2">
                                        {addonEvents.map((addon, addonIdx) => {
                                            const linkedEvent = events.find(ev => ev.id === addon.eventId);
                                            return (
                                                <div key={addonIdx} className="p-2 rounded-lg border bg-indigo-50 border-indigo-200">
                                                    <div className="flex items-start gap-2 mb-2">
                                                        <select
                                                            className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none bg-white"
                                                            value={addon.eventId}
                                                            onChange={(e) => {
                                                                const updated = [...addonEvents];
                                                                const picked = events.find(ev => ev.id === e.target.value);
                                                                updated[addonIdx] = {
                                                                    ...updated[addonIdx],
                                                                    eventId: e.target.value,
                                                                    label: picked ? picked.title : "",
                                                                    price: picked ? picked.price?.toString() || "0" : "0",
                                                                };
                                                                setAddonEvents(updated);
                                                            }}
                                                        >
                                                            <option value="">{t('home.addonSelectEvent')}</option>
                                                            {(() => {
                                                                // Sort: same-day events first, then by date
                                                                const editingDate = editFormData.startDate ? new Date(editFormData.startDate).toDateString() : null;
                                                                const sortedEvents = [...events]
                                                                    .filter(ev => ev.id !== selectedEvent?.id && !addonEvents.some((ae, aeIdx) => aeIdx !== addonIdx && ae.eventId === ev.id))
                                                                    .sort((a, b) => {
                                                                        const aDate = a.startDate ? new Date(a.startDate).toDateString() : null;
                                                                        const bDate = b.startDate ? new Date(b.startDate).toDateString() : null;
                                                                        const aSameDay = aDate === editingDate ? 0 : 1;
                                                                        const bSameDay = bDate === editingDate ? 0 : 1;
                                                                        if (aSameDay !== bSameDay) return aSameDay - bSameDay;
                                                                        return new Date(a.startDate || 0) - new Date(b.startDate || 0);
                                                                    });
                                                                return sortedEvents.map(ev => {
                                                                    const evDate = ev.startDate ? new Date(ev.startDate).toDateString() : null;
                                                                    const isSameDay = evDate === editingDate;
                                                                    return (
                                                                        <option key={ev.id} value={ev.id}>
                                                                            {isSameDay ? "📅 " : ""}{ev.title}{ev.price ? ` ($${ev.price})` : ""}{isSameDay ? ` — ${t('home.addonSameDay')}` : ""}
                                                                        </option>
                                                                    );
                                                                });
                                                            })()}
                                                        </select>
                                                        <button
                                                            type="button"
                                                            onClick={() => setAddonEvents(addonEvents.filter((_, i) => i !== addonIdx))}
                                                            className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors border border-red-200"
                                                        >✕</button>
                                                    </div>
                                                    {addon.eventId && (
                                                        <div className="space-y-1.5">
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs text-gray-600 whitespace-nowrap">{t('home.addonLabel')}:</span>
                                                                <input
                                                                    type="text"
                                                                    className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                                                    placeholder={linkedEvent?.title || "Display name"}
                                                                    value={addon.label}
                                                                    onChange={(e) => {
                                                                        const updated = [...addonEvents];
                                                                        updated[addonIdx] = { ...updated[addonIdx], label: e.target.value };
                                                                        setAddonEvents(updated);
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs text-gray-600 whitespace-nowrap">{t('home.addonPrice')}:</span>
                                                                <input
                                                                    type="number"
                                                                    className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                                                    value={addon.price}
                                                                    onChange={(e) => {
                                                                        const updated = [...addonEvents];
                                                                        updated[addonIdx] = { ...updated[addonIdx], price: e.target.value };
                                                                        setAddonEvents(updated);
                                                                    }}
                                                                    min="0"
                                                                    step="0.01"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={() => setAddonEvents([...addonEvents, { eventId: "", label: "", price: "0" }])}
                                    className="w-full px-3 py-2 border-2 border-dashed border-indigo-300 rounded-lg text-indigo-600 hover:border-indigo-500 hover:bg-indigo-50 transition-colors text-xs font-medium"
                                >{t('home.addonAdd')}</button>
                            </div>

                            {/* Card Style */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Card Style</label>
                                <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    {/* Enable dashed border */}
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" className="w-4 h-4 text-orange-500 border-gray-300 rounded"
                                            checked={!!editFormData.cardStyle?.borderDash}
                                            onChange={e => setEditFormData({ ...editFormData, cardStyle: { ...editFormData.cardStyle, borderDash: e.target.checked } })}
                                        />
                                        <span className="text-sm text-gray-700">Animated dashed border 走马灯边框</span>
                                    </label>

                                    {/* Border color */}
                                    {editFormData.cardStyle?.borderDash && (
                                        <div className="flex flex-col gap-2 pl-2 sm:pl-6">
                                            <label className="text-xs text-gray-600">Border color:</label>
                                            <div className="flex flex-wrap items-center gap-2">
                                                {['#f97316','#ef4444','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ec4899','#000000'].map(c => (
                                                    <button key={c} type="button"
                                                        onClick={() => setEditFormData({ ...editFormData, cardStyle: { ...editFormData.cardStyle, borderColor: c } })}
                                                        className={`w-7 h-7 rounded-full border-2 transition-transform ${editFormData.cardStyle?.borderColor === c ? 'border-gray-800 scale-125' : 'border-transparent hover:scale-110'}`}
                                                        style={{ backgroundColor: c }}
                                                    />
                                                ))}
                                                <input type="color" className="w-7 h-7 rounded cursor-pointer border border-gray-300"
                                                    value={editFormData.cardStyle?.borderColor || '#f97316'}
                                                    onChange={e => setEditFormData({ ...editFormData, cardStyle: { ...editFormData.cardStyle, borderColor: e.target.value } })}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Marquee text */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs text-gray-600">Marquee text 滚动文字 <span className="text-gray-400">(leave empty to hide)</span></label>
                                        <div className="relative">
                                            <input type="text" placeholder="e.g. 🔥 Limited spots! 名额有限 🔥"
                                                className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                                                value={editFormData.cardStyle?.marqueeText || ''}
                                                onChange={e => setEditFormData({ ...editFormData, cardStyle: { ...editFormData.cardStyle, marqueeText: e.target.value } })}
                                            />
                                            {editFormData.cardStyle?.marqueeText && (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditFormData({ ...editFormData, cardStyle: { ...editFormData.cardStyle, marqueeText: '' } })}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 hover:text-gray-700 transition-colors"
                                                    title="Clear marquee text"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Marquee speed */}
                                    {editFormData.cardStyle?.marqueeText && (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <label className="text-xs text-gray-600 whitespace-nowrap">Speed 速度:</label>
                                            {['slow','normal','fast'].map(s => (
                                                <button key={s} type="button"
                                                    onClick={() => setEditFormData({ ...editFormData, cardStyle: { ...editFormData.cardStyle, marqueeSpeed: s } })}
                                                    className={`px-2 py-1 text-xs rounded capitalize transition-colors ${editFormData.cardStyle?.marqueeSpeed === s ? 'bg-orange-500 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:border-orange-300'}`}
                                                >{s === 'slow' ? '慢' : s === 'normal' ? '中' : '快'} {s}</button>
                                            ))}
                                        </div>
                                    )}

                                    {/* Live preview */}
                                    {(editFormData.cardStyle?.borderDash || editFormData.cardStyle?.marqueeText) && (
                                        <div className="pt-1">
                                            <p className="text-xs text-gray-400 mb-1">Preview:</p>
                                            <div className={`rounded-lg overflow-hidden text-xs text-gray-400 italic p-2 ${editFormData.cardStyle?.borderDash ? 'dashed-border-animated' : 'border border-gray-200'}`}
                                                style={editFormData.cardStyle?.borderDash ? { '--cbd': editFormData.cardStyle?.borderColor || '#f97316' } : {}}>
                                                {editFormData.cardStyle?.marqueeText ? (
                                                    <div className="overflow-hidden relative h-5">
                                                        <span className={`absolute whitespace-nowrap text-xs font-semibold marquee-${editFormData.cardStyle?.marqueeSpeed || 'normal'}`}
                                                            style={{ color: editFormData.cardStyle?.borderColor || '#f97316', top: '2px' }}>
                                                            {editFormData.cardStyle.marqueeText}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span>Event card preview</span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Additional Services */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">{t('home.additionalServices')}</label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <span className="text-xs text-gray-600">{t('home.enableServices')}</span>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={editFormData.enableServices !== false}
                                                onChange={(e) => setEditFormData({...editFormData, enableServices: e.target.checked})}
                                            />
                                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-500"></div>
                                        </div>
                                    </label>
                                </div>
                                {editFormData.enableServices !== false && (
                                    <>
                                        <div className="space-y-2">
                                        {signupOptions.map((option, index) => {
                                            const isPlayerTicket = option.isPlayerTicket || false;
                                            return (
                                            <div key={index} className={`p-2 rounded-lg border ${isPlayerTicket ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'}`}>
                                                {isPlayerTicket && (
                                                    <div className="flex items-center gap-1 mb-1.5 text-indigo-700">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                        <span className="text-[10px] font-semibold uppercase tracking-wide">Player Ticket</span>
                                                    </div>
                                                )}
                                                {/* Service Name - Full Width */}
                                                <div className="mb-2">
                                                    <input
                                                        type="text"
                                                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                                                        placeholder={isPlayerTicket ? "e.g., Additional Player" : "Service name (e.g., Guest Ticket)"}
                                                        value={option.name}
                                                        onChange={(e) => handleSignupOptionChange(index, "name", e.target.value)}
                                                    />
                                                </div>
                                                
                                                {/* Price and Allow Qty Row */}
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <div className="relative w-20">
                                                        <span className="absolute left-2 top-2 text-xs text-gray-500">$</span>
                                                        <input
                                                            type="number"
                                                            className="w-full pl-5 pr-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none disabled:bg-gray-100 disabled:text-gray-500"
                                                            value={option.price}
                                                            onChange={(e) => handleSignupOptionChange(index, "price", e.target.value)}
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                    {!isPlayerTicket && (
                                                        <label className="flex items-center gap-1 text-xs whitespace-nowrap cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                className="w-3.5 h-3.5 text-orange-600 border-gray-300 rounded focus:ring-1 focus:ring-orange-500"
                                                                checked={option.allowQuantity || false}
                                                                onChange={(e) => handleSignupOptionChange(index, "allowQuantity", e.target.checked)}
                                                            />
                                                            <span className="text-gray-600">Allow qty</span>
                                                        </label>
                                                    )}
                                                    {(isPlayerTicket || option.allowQuantity) && (
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-xs text-gray-500">Max:</span>
                                                            <input
                                                                type="number"
                                                                className="w-14 px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                                                                value={option.maxQuantity || (isPlayerTicket ? "5" : "10")}
                                                                onChange={(e) => handleSignupOptionChange(index, "maxQuantity", e.target.value)}
                                                                min="1"
                                                                max="99"
                                                            />
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSignupOption(index)}
                                                        className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors border border-red-200 ml-auto"
                                                        disabled={signupOptions.length === 1}
                                                    >{t('common.delete')}</button>
                                                </div>
                                                
                                                {/* Per-service options: Required, Player Names & Notes */}
                                                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-200 flex-wrap">
                                                    <label className="flex items-center gap-1 text-xs whitespace-nowrap cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="w-3.5 h-3.5 text-orange-600 border-gray-300 rounded focus:ring-1 focus:ring-orange-500"
                                                            checked={option.required || false}
                                                            onChange={(e) => handleSignupOptionChange(index, "required", e.target.checked)}
                                                        />
                                                        <span className="font-medium text-orange-700">{t('common.required')}</span>
                                                    </label>
                                                    {!isPlayerTicket && (
                                                        <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                                                            <span className="text-gray-600">Group:</span>
                                                            <input
                                                                type="text"
                                                                className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-transparent outline-none"
                                                                placeholder="choose-one"
                                                                value={option.requiredGroup || ""}
                                                                onChange={(e) => handleSignupOptionChange(index, "requiredGroup", e.target.value)}
                                                                title="Same group name on 2+ options = players must pick exactly one (e.g. 女生票价 / 男生票价)"
                                                            />
                                                        </label>
                                                    )}
                                                    <label className={`flex items-center gap-1 text-xs whitespace-nowrap ${isPlayerTicket ? 'opacity-60' : 'cursor-pointer'}`}>
                                                        <input
                                                            type="checkbox"
                                                            className="w-3.5 h-3.5 text-orange-600 border-gray-300 rounded focus:ring-1 focus:ring-orange-500"
                                                            checked={isPlayerTicket ? true : (option.requirePlayerNames || false)}
                                                            onChange={(e) => handleSignupOptionChange(index, "requirePlayerNames", e.target.checked)}
                                                            disabled={isPlayerTicket}
                                                        />
                                                        <span className="text-gray-600">Player names</span>
                                                    </label>
                                                    <label className="flex items-center gap-1 text-xs whitespace-nowrap cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="w-3.5 h-3.5 text-orange-600 border-gray-300 rounded focus:ring-1 focus:ring-orange-500"
                                                            checked={option.requireNotes || false}
                                                            onChange={(e) => handleSignupOptionChange(index, "requireNotes", e.target.checked)}
                                                        />
                                                        <span className="text-gray-600">{t('home.notes')}</span>
                                                    </label>
                                                </div>
                                            </div>
                                            );
                                        })}
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                type="button"
                                                onClick={addSignupOption}
                                                className="flex-1 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-500 hover:text-orange-500 transition-colors text-xs font-medium"
                                            >{t('home.addService')}</button>
                                            <button
                                                type="button"
                                                onClick={addPlayerTicketOption}
                                                className="flex-1 px-3 py-2 border-2 border-dashed border-indigo-300 rounded-lg text-indigo-600 hover:border-indigo-500 hover:bg-indigo-50 transition-colors text-xs font-medium"
                                            >{t('home.addPlayerTicket')}</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-4 flex gap-3">
                            <button
                                onClick={() => setShowEditModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                disabled={savingEvent}
                            >{t('common.cancel')}</button>
                            <button
                                onClick={handleSaveEvent}
                                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 font-medium"
                                disabled={savingEvent}
                            >
                                {savingEvent ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal - Mobile Friendly */}
            {showDeleteModal && isAdmin && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full mx-4 p-6">
                        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">{t('home.deleteEvent')}</h3>
                        <p className="text-gray-600 text-center mb-6 text-sm">{t('common.delete')}<strong>"{selectedEvent?.title}"</strong>? This cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                disabled={deletingEvent}
                            >{t('common.cancel')}</button>
                            <button
                                onClick={confirmDeleteEvent}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 font-medium"
                                disabled={deletingEvent}
                            >
                                {deletingEvent ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Modal */}
            {selectedEventForShare && (
                <ShareModal
                    event={selectedEventForShare}
                    venue={null}
                    registrationCount={selectedEventForShare.currentParticipants || 0}
                    isOpen={showShareModal}
                    onClose={() => {
                        setShowShareModal(false);
                        setSelectedEventForShare(null);
                    }}
                />
            )}

            {/* Share All Upcoming Modal */}
            {showShareAllModal && isAdmin && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-[9999]">
                    <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg flex flex-col overflow-hidden" style={{ maxHeight: '90dvh' }}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>{t('home.shareAllUpcoming')}</h3>
                            <button
                                onClick={() => setShowShareAllModal(false)}
                                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Language Toggle */}
                        <div className="px-5 pt-4 pb-2 flex-shrink-0">
                            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
                                <button
                                    onClick={() => handleShareAllLanguageSwitch('en')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                        shareAllLanguage === 'en'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    🇦🇺 English
                                </button>
                                <button
                                    onClick={() => handleShareAllLanguageSwitch('zh')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                        shareAllLanguage === 'zh'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    🇨🇳 中文
                                </button>
                            </div>
                            <p className="text-xs text-gray-400 mt-2">{t('home.editBeforeCopy')}</p>
                        </div>

                        {/* Editable Preview */}
                        <div className="flex-1 overflow-y-auto px-5 pb-3 min-h-0">
                            <textarea
                                className="w-full h-full min-h-[220px] p-4 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 resize-none leading-relaxed font-mono"
                                value={shareAllText}
                                onChange={(e) => setShareAllText(e.target.value)}
                                spellCheck={false}
                            />
                        </div>

                        {/* Footer Actions */}
                        <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100 flex gap-3">
                            <button
                                onClick={() => setShowShareAllModal(false)}
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >{t('common.cancel')}</button>
                            <button
                                onClick={handleCopyShareAll}
                                className="flex-2 px-6 py-3 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors flex items-center gap-2 justify-center"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>{t('home.copyToClipboard')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Click outside to close admin menu */}
            {showAdminMenu && (
                <div 
                    className="fixed inset-0 z-30" 
                    onClick={() => setShowAdminMenu(false)}
                />
            )}

            {/* Waitlist Promotion Modal */}
            {waitlistPromoteModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setWaitlistPromoteModal(null)}>
                    <div className="bg-white rounded-2xl max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="p-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900">{lang === 'zh' ? '容量已增加' : 'Capacity Increased'}</h3>
                                    <p className="text-xs text-gray-500">{waitlistPromoteModal.eventTitle}</p>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-xl p-3 mb-4 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">{lang === 'zh' ? '容量变化' : 'Capacity'}</span>
                                    <span className="font-semibold">{waitlistPromoteModal.oldMax} → {waitlistPromoteModal.newMax}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">{lang === 'zh' ? '新增空位' : 'New spots'}</span>
                                    <span className="font-semibold text-green-600">{waitlistPromoteModal.toPromote}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">{lang === 'zh' ? '候补等待中' : 'On waitlist'}</span>
                                    <span className="font-semibold text-orange-600">{waitlistPromoteModal.waitlistCount}</span>
                                </div>
                            </div>

                            <div className={`rounded-xl p-3 mb-4 text-xs ${waitlistPromoteModal.isPaid ? 'bg-blue-50 border border-blue-200 text-blue-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>
                                {waitlistPromoteModal.isPaid ? (
                                    <>
                                        <p className="font-semibold mb-1">{lang === 'zh' ? '💰 付费活动' : '💰 Paid Event'}</p>
                                        <p>{lang === 'zh'
                                            ? `系统已自动通知前 ${waitlistPromoteModal.toPromote} 位候补（邮件 + App 通知），需在活动开始前付款确认，超时未付则名额给下一位。`
                                            : `The first ${waitlistPromoteModal.toPromote} on the waitlist have been notified automatically (email + app). They must pay before the event starts, otherwise the spot moves to the next person.`}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="font-semibold mb-1">{lang === 'zh' ? '免费活动' : 'Free Event'}</p>
                                        <p>{lang === 'zh'
                                            ? `系统已自动把前 ${waitlistPromoteModal.toPromote} 位候补转为正式报名，并通知了他们。`
                                            : `The first ${waitlistPromoteModal.toPromote} on the waitlist have been confirmed and notified automatically.`}
                                        </p>
                                    </>
                                )}
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={handlePromoteWaitlist}
                                    className="flex-1 px-4 py-2.5 rounded-xl bg-green-600 text-white font-medium text-sm hover:bg-green-700"
                                >{lang === 'zh' ? '知道了' : 'OK'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Home;